import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, money, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useLeviWorkspaceHint } from "@/lib/leviWorkspace";
import { Plus, Sparkles, ShieldCheck, Send, Trash2, ArrowLeft, Save, Camera, Languages, AlertTriangle, Info, PackageOpen, X, Search, Link2, ArrowDown, ArrowUp, Copy, Clock, MoreVertical } from "lucide-react";
import PhotoUploader from "@/components/PhotoUploader";
import { findFieldsWithUnresolvedPlaceholders } from "@/lib/documentPresets";
import RoofingSimpleBuilder from "@/components/RoofingSimpleBuilder";
import LeviCoworkerCard from "@/components/LeviCoworkerCard";
import WorkAreaStagingPicker from "@/components/estimates/WorkAreaStagingPicker";
import LeviScopeOrchestrator from "@/components/estimates/LeviScopeOrchestrator";
import LeviEstimateSuggestionsSection from "@/components/estimate/LeviEstimateSuggestionsSection";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const EstimatesList = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [projects, setProjects] = useState([]);
  // Slice 2J · Row navigation is the "Open" affordance. The overflow
  // menu only presents actions that are NOT the row's default: safe
  // duplicate + safe delete draft. No competing Open/Edit item.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(null);
  const refresh = () => api.get("/estimates").then((r) => setEstimates(r.data));
  useEffect(() => {
    Promise.all([api.get("/estimates"), api.get("/projects")]).then(([e, p]) => {
      setEstimates(e.data); setProjects(p.data);
    });
  }, []);
  const projMap = Object.fromEntries(projects.map((p) => [p.id, p]));

  const openEstimate = (id) => navigate(`/app/estimates/${id}`);

  const handleRowKeyDown = (e, id) => {
    // Enter or Space activates the row. Space is scrolled by default —
    // preventDefault keeps the page from jumping.
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      openEstimate(id);
    }
  };

  const duplicateEstimate = async (est) => {
    if (duplicating) return;
    setDuplicating(est.id);
    try {
      const { data } = await api.post(`/estimates/${est.id}/duplicate`);
      toast.success(t("estimates_form.toast_duplicated"));
      navigate(`/app/estimates/${data.id}`);
    } catch {
      toast.error(t("estimates_form.toast_duplicate_failed"));
    } finally {
      setDuplicating(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/estimates/${deleteTarget.id}`);
      toast.success(t("estimates_form.toast_deleted"));
      setDeleteTarget(null);
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Backend returns { error, reason, conflicts?, message } on 409.
      const reason = detail?.reason;
      const message = detail?.message || t("estimates_form.toast_delete_failed");
      setDeleteError({ reason, message, conflicts: detail?.conflicts || [] });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardLayout
      title={t("pages.estimates.title")}
      subtitle={t("pages.estimates.subtitle")}
      actions={
        <Link to="/app/estimates/new" data-testid="estimates-new-btn" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-blue">
          <Plus size={14} /> {t("pages.estimates.new_btn")}
        </Link>
      }
    >
      {estimates.length === 0 ? (
        <div className="jc-card p-12 text-center">
          <div className="font-display text-xl">{t("estimates_form.list_empty_title")}</div>
          <p className="text-sm text-[var(--jc-text-soft)] mt-2">{t("estimates_form.list_empty_body")}</p>
        </div>
      ) : (
        <div className="jc-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--jc-bg)] text-[var(--jc-text-soft)]">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">{t("estimates_form.col_customer")}</th>
                <th className="text-left px-5 py-3 font-semibold">{t("estimates_form.col_lines")}</th>
                <th className="text-left px-5 py-3 font-semibold">{t("estimates_form.col_total")}</th>
                <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">{t("estimates_form.col_status")}</th>
                <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">{t("estimates_form.col_created")}</th>
                <th className="w-10 px-2" aria-hidden />
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--jc-border)]">
              {estimates.map((e) => (
                <tr
                  key={e.id}
                  data-testid={`estimate-row-${e.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => openEstimate(e.id)}
                  onKeyDown={(ev) => handleRowKeyDown(ev, e.id)}
                  aria-label={t("estimates_form.row_open_aria", projMap[e.project_id]?.customer_name || "—")}
                  className="hover:bg-[var(--jc-bg)] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)] focus-visible:bg-[var(--jc-bg)]"
                >
                  <td className="px-5 py-3 font-semibold">{projMap[e.project_id]?.customer_name || "—"}</td>
                  <td className="px-5 py-3">{e.line_items?.length || 0}</td>
                  <td className="px-5 py-3 font-mono-jc">{money(e.total)}</td>
                  <td className="px-5 py-3 hidden md:table-cell"><span className="text-xs px-2 py-1 rounded-full bg-[var(--jc-border)]">{t(`estimates_form.status_${e.status}`) === `estimates_form.status_${e.status}` ? e.status : t(`estimates_form.status_${e.status}`)}</span></td>
                  <td className="px-5 py-3 hidden md:table-cell text-[var(--jc-text-soft)]">{formatDate(e.created_at)}</td>
                  <td
                    className="px-2 py-3 text-right"
                    // Prevent row-level click/keydown propagation from
                    // reaching the parent <tr> whenever the contractor
                    // interacts with the overflow menu.
                    onClick={(ev) => ev.stopPropagation()}
                    onKeyDown={(ev) => ev.stopPropagation()}
                  >
                    <EstimateRowMenu
                      estimate={e}
                      duplicating={duplicating === e.id}
                      onDuplicate={() => duplicateEstimate(e)}
                      onDelete={() => { setDeleteError(null); setDeleteTarget(e); }}
                      t={t}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteError(null); } }}
      >
        <AlertDialogContent data-testid="delete-estimate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[var(--jc-red)]/10 text-[var(--jc-red)]">
                <AlertTriangle size={18} />
              </span>
              {t("estimates_form.delete_dialog_title", projMap[deleteTarget?.project_id]?.customer_name || "—")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("estimates_form.delete_dialog_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <div
              data-testid="delete-estimate-conflict"
              className="mt-2 rounded-md border border-[var(--jc-red)]/30 bg-[var(--jc-red)]/5 px-3 py-2 text-sm text-[var(--jc-red)]"
            >
              {deleteError.message}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-estimate-cancel-btn" disabled={deleting}>
              {t("estimates_form.delete_dialog_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-estimate-confirm-btn"
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-[var(--jc-red)] text-white hover:bg-[var(--jc-red)]/90 focus:ring-[var(--jc-red)]"
            >
              {deleting ? t("estimates_form.delete_dialog_deleting") : t("estimates_form.delete_dialog_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

// Slice 2J · Estimate row overflow menu.
// Renders exactly the actions that are NOT the row's default "Open"
// affordance: Duplicate (always) and Delete Draft (only when
// status === "draft"). No Open, no Edit — the row itself is the
// Open path, and an entry for it would recreate the redundant-action
// problem Slice 2J removes.
const EstimateRowMenu = ({ estimate, duplicating, onDuplicate, onDelete, t }) => {
  const isDraft = estimate?.status === "draft";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={`estimate-row-menu-${estimate.id}`}
          aria-label={t("estimates_form.row_menu_aria")}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-transparent text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] hover:bg-[var(--jc-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuItem
          data-testid={`estimate-row-duplicate-${estimate.id}`}
          disabled={duplicating}
          onSelect={(e) => { e.preventDefault(); onDuplicate(); }}
        >
          <Copy size={14} className="mr-2" />
          {duplicating ? t("estimates_form.row_duplicating") : t("estimates_form.row_duplicate")}
        </DropdownMenuItem>
        {isDraft && (
          <DropdownMenuItem
            data-testid={`estimate-row-delete-${estimate.id}`}
            className="text-[var(--jc-red)] focus:text-[var(--jc-red)]"
            onSelect={(e) => { e.preventDefault(); onDelete(); }}
          >
            <Trash2 size={14} className="mr-2" />
            {t("estimates_form.row_delete_draft")}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Slice 2J · Ask Once, Reuse Everywhere — compact "From this job"
// cue for the estimate editor. Contractor-controlled: only offers the
// prefill; never mutates the project; never overwrites populated /
// contractor-edited estimate notes; dismissal leaves notes unchanged.
// The prefill text is the smallest useful repository-supported summary
// (only project fields that already exist are joined).
const buildJobReuseSummary = (project) => {
  if (!project || typeof project !== "object") return "";
  const parts = [];
  const jobDesc = (project.job_description || "").trim();
  const measurements = (project.measurements || "").trim();
  const special = (project.special_conditions || "").trim();
  if (jobDesc) parts.push(`Job: ${jobDesc}`);
  if (measurements) parts.push(`Measurements: ${measurements}`);
  if (special) parts.push(`Special conditions: ${special}`);
  return parts.join("\n");
};

const EstimateNotesReuseCue = ({ estimate, project, onPrefill, t }) => {
  const [dismissed, setDismissed] = useState(false);
  const notesEmpty = !((estimate?.notes || "").trim());
  const summary = buildJobReuseSummary(project);
  if (dismissed) return null;
  if (!notesEmpty) return null;
  if (!summary) return null;
  return (
    <div
      data-testid="estimate-notes-reuse-cue"
      className="jc-card px-3 py-2 flex flex-wrap items-center gap-2 text-sm border-l-4 border-l-[var(--jc-blue)] bg-[var(--jc-blue)]/5"
    >
      <span className="text-[var(--jc-text-soft)]">
        {t("estimates_form.reuse_cue_label")}
      </span>
      <button
        type="button"
        data-testid="estimate-notes-reuse-prefill-btn"
        onClick={() => onPrefill(summary)}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-md jc-btn-primary"
      >
        {t("estimates_form.reuse_cue_action")}
      </button>
      <button
        type="button"
        data-testid="estimate-notes-reuse-dismiss-btn"
        onClick={() => setDismissed(true)}
        aria-label={t("estimates_form.reuse_cue_dismiss_aria")}
        className="ml-auto text-xs text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"
      >
        {t("estimates_form.reuse_cue_dismiss")}
      </button>
    </div>
  );
};

const UNIT_OPTIONS = [
  "each", "sq ft", "square", "LF", "bundle", "roll", "box", "gallon",
  "cubic yard", "ton", "sheet", "board", "board-ft", "panel", "stair",
  "room", "fixture", "opening", "door", "window", "coat", "layer",
  "trip", "day", "hour", "job", "allowance",
];

const newLine = () => ({
  id: crypto.randomUUID(), name: "", description: "", quantity: 1, unit: "each",
  material_basis_unit: "each", material_qty: 1, material_cost: 0,
  labor_basis: "hour", labor_qty: 0, labor_unit_cost: 0,
  labor_hours: 0, labor_cost: 0,
  markup_pct: 25, total: 0, notes: "",
  needs_pricing: false, needs_material_pricing: false, material_required: false,
});

// Compute the labor total for a single line, preferring the new basis fields when present.
const lineLaborTotal = (l) => {
  const basis = (l?.labor_basis || "").trim();
  const qty = parseFloat(l?.labor_qty ?? 0);
  const rate = parseFloat(l?.labor_unit_cost ?? 0);
  if (basis && qty > 0 && rate > 0) return qty * rate;
  return parseFloat(l?.labor_cost ?? 0) || 0;
};
const lineMaterialTotal = (l) => {
  // Prefer material_qty (in material_basis_unit) when present; fall back to quantity.
  const mq = l?.material_qty;
  const qty = (mq === null || mq === undefined || mq === "" || Number.isNaN(parseFloat(mq)))
    ? (parseFloat(l?.quantity ?? 1) || 1)
    : parseFloat(mq);
  const rate = parseFloat(l?.material_cost ?? 0) || 0;
  return qty * rate;
};

// Slice F fixed-price contribution: unit_quantity × unit_price OR flat_price.
// Mirrors backend _normalize_line_items_for_pricing_method — used for both
// pricing-mode aware "unpriced" detection and the live sidebar total preview.
const lineFixedPriceTotal = (l) => {
  const pm = (l?.pricing_method || "").trim().toLowerCase();
  if (pm === "unit_price") {
    const q = parseFloat(l?.unit_quantity ?? 0) || 0;
    const p = parseFloat(l?.unit_price ?? 0) || 0;
    return q * p;
  }
  if (pm === "flat") {
    return parseFloat(l?.flat_price ?? 0) || 0;
  }
  return 0;
};
const isFixedPriceLine = (l) => {
  const pm = (l?.pricing_method || "").trim().toLowerCase();
  return pm === "unit_price" || pm === "flat";
};

// Slice 2B1 — Approved installed-selling line from Trade Setup.
// These lines carry `pricing_type === "installed_selling"` plus a tenant-
// owned `pricing_rule_id`. The math is simply: quantity × selling_price_per_unit
// (matching backend compute_estimate_totals). Material + labor + markup fields
// are IRRELEVANT and must not be rendered or included in totals.
const isInstalledSellingLine = (l) =>
  (l?.pricing_type || "").trim().toLowerCase() === "installed_selling"
  || l?.is_installed_selling === true;

const lineInstalledSellingTotal = (l) => {
  const q = parseFloat(l?.quantity ?? 0) || 0;
  const spu = parseFloat(l?.selling_price_per_unit ?? 0) || 0;
  return q * spu;
};

// Slice 2B1 — Distinct classification for approved-pricing lines with a
// missing / zero quantity. These are NOT unpriced rows (the price is
// approved); they only need a quantity. The estimate banners must show
// "Enter a quantity for N approved-pricing line(s)" — never
// "Material pricing is incomplete" or "Needs pricing".
const isLineNeedsQuantity = (l) => {
  if (!isInstalledSellingLine(l)) return false;
  const q = parseFloat(l?.quantity ?? 0) || 0;
  return q <= 0;
};

// Effective line total for the sidebar live-preview compute.
// Mirrors backend: installed_selling → qty × spu; fixed-price mode overrides
// everything; else material + labor with markup.
const lineEffectiveTotal = (l) => {
  if (isInstalledSellingLine(l)) return lineInstalledSellingTotal(l);
  if (isFixedPriceLine(l)) return lineFixedPriceTotal(l);
  const mat = lineMaterialTotal(l);
  const lab = lineLaborTotal(l);
  const markup = parseFloat(l?.markup_pct ?? 0) || 0;
  return (mat + lab) * (1 + markup / 100);
};

// A line is "unpriced" if it produces $0 of billable total AND the
// underlying pricing shape is unresolved. Installed_selling lines with a
// missing quantity are NOT classified here — they use `isLineNeedsQuantity`
// so banners can differentiate. For fixed-price lines (Slice F unit_price
// / flat) unpriced means unit_quantity × unit_price (or flat_price) is 0.
// For standard lines, both material and labor totals are zero.
const isLineUnpriced = (l) => {
  if (isInstalledSellingLine(l)) return false;   // routed via isLineNeedsQuantity
  if (isFixedPriceLine(l)) return lineFixedPriceTotal(l) <= 0;
  return (lineMaterialTotal(l) + lineLaborTotal(l)) <= 0;
};
// "Needs material pricing" — the row consumes materials (per AI or contractor's profile)
// but the material total is still $0. Pure-labor rows (material_required=false) don't
// trigger this. Fixed-price lines are contractor-set sell prices — they are NEVER
// treated as "material pricing incomplete" (the contractor's rate is the sell price).
// Installed_selling lines never track material separately.
const isLineNeedsMaterial = (l) => {
  if (isInstalledSellingLine(l)) return false;
  if (isFixedPriceLine(l)) return false;
  const required = l?.material_required === true || l?.needs_material_pricing === true;
  return required && lineMaterialTotal(l) <= 0;
};
// A line is "incomplete" if it has no name, or no pricing at all, or material is still missing.
const isLineIncomplete = (l) => {
  if (!((l?.name || "").trim())) return true;
  if (isLineUnpriced(l)) return true;
  if (isLineNeedsMaterial(l)) return true;
  return false;
};

export const EstimateDetail = () => {
  const { eid } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang, t } = useLang();
  // Phase 5A · Publish current-page workspace hint (backend
  // re-verifies the ID against the authenticated tenant).
  useLeviWorkspaceHint("estimate_detail", {
    open_document_type: "estimate",
    open_document_id: eid,
    current_section: "estimate_detail",
  });
  // Slice 2J amendment · Centered Levi acknowledgement after Edit
  // Estimate save. Set to true when the estimate page loads with the
  // ?levi_ack=1 query flag (planted by NewEstimate.jsx on save-return).
  // Auto-dismisses after 2500 ms and clears the flag from the URL.
  const [leviAck, setLeviAck] = useState(false);
  useEffect(() => {
    if (searchParams.get("levi_ack") !== "1") return;
    setLeviAck(true);
    const timer = setTimeout(() => {
      setLeviAck(false);
      const next = new URLSearchParams(searchParams);
      next.delete("levi_ack");
      setSearchParams(next, { replace: true });
    }, 5000);
    return () => clearTimeout(timer);
  }, [searchParams, setSearchParams]);
  const [estimate, setEstimate] = useState(null);
  const [project, setProject] = useState(null);
  const [profile, setProfile] = useState(null);
  const [outputLang, setOutputLang] = useState(lang);
  const [running, setRunning] = useState({ scope: false, check: false, save: false });
  // Banner shown after AI Scope Organizer runs — tells the contractor how many
  // suggested rows were added and reminds them these are drafts.
  const [scopeBanner, setScopeBanner] = useState(null); // { added: number } | { added: 0 } | null
  // Proposal guard dialog (open when estimate is $0 or has unpriced/incomplete rows).
  const [proposalGuard, setProposalGuard] = useState(null); // { issues: string[] } | null
  const [sendingProposal, setSendingProposal] = useState(false);
  // Material basis conversion prompt (opens when contractor switches basis to a non-matching unit).
  const [basisPrompt, setBasisPrompt] = useState(null); // { idx, qtyUnit, newBasis, qty }
  // Which line index (if any) has the Price Book picker modal open.
  const [priceBookPickerIdx, setPriceBookPickerIdx] = useState(null);
  // Levi-Assisted Measurement Review + Contractor-Controlled Quantity
  // Staging · which line index (if any) has the work-area staging
  // picker open. Modal-scoped; never persists to storage.
  const [waStagingIdx, setWaStagingIdx] = useState(null);
  // Slice A3.3b — Contractor-controlled AI advisory toggle. Persisted in
  // localStorage only. Default OFF so first-time contractors get the same
  // deterministic-only Double Check they have today.
  const [includeAiAdvisory, setIncludeAiAdvisory] = useState(() => {
    try {
      return localStorage.getItem("jc.double_check.include_ai") === "1";
    } catch {
      return false;
    }
  });
  const toggleIncludeAiAdvisory = (next) => {
    setIncludeAiAdvisory(next);
    try {
      localStorage.setItem("jc.double_check.include_ai", next ? "1" : "0");
    } catch {
      /* ignore quota / private-mode errors */
    }
  };

  // Slice A3.1 — Deterministic Double Check.
  const [runningDoubleCheck, setRunningDoubleCheck] = useState(false);
  // iter45 · Edit Polish v1 — track whether the local estimate has edits
  // that have not been PUT to the server yet. Flipped true by any mutation
  // helper below; flipped false after a successful save() (or after the
  // server-side full replace that organizeWithAI / runCheck / runDoubleCheck
  // perform). Purely client-side.
  const [dirty, setDirty] = useState(false);
  const markDirty = () => setDirty(true);
  // iter45 · Proposal-drift teaser data. On mount + after save, look up
  // the most-recent proposal doc for this estimate (uses existing
  // GET /api/proposals — no new endpoint) so we can non-blocking-warn the
  // contractor if the current estimate is newer than the sent proposal.
  const [sentProposal, setSentProposal] = useState(null);
  // iter57 · Slice 1B — When Estimate PUT returns 409 document_locked,
  // this holds the structured backend payload so we can render a
  // contractor-friendly banner with the "Create New Version" action.
  const [lockedBanner, setLockedBanner] = useState(null);
  // iter34 (Rio finding #9) — DOM ref for the Double Check findings panel
  // so we can (a) auto-scroll to it after a run that returns red/yellow
  // severity and (b) let a persistent "View warnings ↓" button jump to it
  // any time. Purely a viewport-navigation affordance — never mutates
  // estimate state, never calls any endpoint, never touches line items.
  const doubleCheckPanelRef = useRef(null);
  const scrollToDoubleCheckPanel = () => {
    // 100ms defer lets the panel finish rendering after setEstimate.
    setTimeout(() => {
      doubleCheckPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };
  // iter35 (Levi MVP) — parent-owned handler Levi can call to jump the
  // contractor to the first line whose material is unpriced. Pure DOM
  // scroll; never mutates state.
  const scrollToFirstMissingMaterial = () => {
    const lines = estimate?.line_items || [];
    const idx = lines.findIndex((l) => (
      l?.needs_material_pricing === true ||
      (l?.material_required === true && (!l?.material_cost || l.material_cost <= 0))
    ));
    if (idx < 0) return;
    setTimeout(() => {
      const node = document.querySelector(`[data-testid="line-material-math-${idx}"]`);
      if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };
  // JSON snapshot of the estimate the last time Double Check ran. Used purely
  // to render a "stale" indicator when the contractor edits after running.
  // Contractor-internal state; never sent to the server.
  const [lastCheckedSnapshot, setLastCheckedSnapshot] = useState(null);
  // Slice A3.2 — Acknowledgement dialog. Opens when the backend proposal
  // create returns 409 with double_check_required. Holds the fresh
  // deterministic Double Check payload so the RED findings can be shown.
  const [ackDialog, setAckDialog] = useState(null); // { doubleCheck } | null
  const [ackConfirmed, setAckConfirmed] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  // iter54 · P0 Pricing Readiness — Account-level readiness snapshot,
  // fetched read-only from /onboarding-status. Drives ONLY the Estimate
  // Editor banner + link to Pricing Profile. Never blocks proposal
  // generation — that gate lives in POST /api/proposals and evaluates
  // the CURRENT estimate, not the account.
  const [accountReadiness, setAccountReadiness] = useState(null);
  useEffect(() => {
    api.get("/onboarding-status")
      .then((r) => setAccountReadiness(r.data || null))
      .catch(() => { /* non-fatal — banner just won't render */ });
  }, []);

  // Slice 2B1 (Workstream A) — Automatic approved-pricing staging.
  // Fires ONCE per estimate open after project + estimate load. Tries
  // the locked Roofing endpoint first (preserves the pilot contract);
  // if Roofing returns ``no_match`` we then attempt each Phase 1
  // cohort trade (Flooring, Painting, Drywall, Siding, Insulation)
  // against the tenant's approved reusable installed-selling rules.
  // The generic endpoint returns ``no_match`` cheaply when the tenant
  // has no rules for that trade, so this loop is safe and idempotent.
  // A ref guards against React 18 dev double-invoke and route re-mount.
  const autoStageAttemptedRef = useRef(new Set());
  const [autoStageMultiple, setAutoStageMultiple] = useState(null);
  useEffect(() => {
    if (!eid || !estimate?.id || !project?.id) return;
    if (autoStageAttemptedRef.current.has(eid)) return;
    autoStageAttemptedRef.current.add(eid);
    (async () => {
      const applyAutoStagedLine = (body) => {
        if (body?.result === "auto_staged" && body.line) {
          setEstimate((prev) => {
            const items = prev?.line_items || [];
            if (items.some((li) => li && li.pricing_rule_id === body.pricing_rule_id)) return prev;
            return { ...prev, line_items: [...items, body.line] };
          });
          toast.success(t("estimates_form.toast_matched", body.system_type), { duration: 5000 });
          return true;
        }
        if (body?.result === "multiple_matches") {
          setAutoStageMultiple(body.candidates || []);
          return true;
        }
        return false;
      };
      // 1) Roofing pilot — untouched contract.
      try {
        const r = await api.post(
          `/onboarding/trade-setup/estimates/${eid}/auto-stage-roofing`,
        );
        if (applyAutoStagedLine(r.data || {})) return;
      } catch (e) {
        // Preserve estimate data + fallback controls. Do not crash.
      }
      // 2) Phase 1 cohort trades. Each call is scoped to the caller's
      //    tenant + trade and short-circuits at the first confident
      //    match. ``no_match`` on every cohort trade leaves the safe
      //    unpriced/fallback path in place.
      const COHORT_TRADES = ["Flooring", "Painting", "Drywall", "Siding", "Insulation"];
      for (const trade of COHORT_TRADES) {
        try {
          const r = await api.post(
            `/onboarding/trade-setup/estimates/${eid}/auto-stage/${encodeURIComponent(trade)}`,
          );
          if (applyAutoStagedLine(r.data || {})) return;
        } catch (e) {
          // Non-fatal — try the next cohort trade.
        }
      }
    })();
  }, [eid, estimate?.id, project?.id]);

  useEffect(() => {
    api.get(`/estimates/${eid}`).then(async (r) => {
      setEstimate(r.data);
      try {
        const p = await api.get(`/projects/${r.data.project_id}`);
        setProject(p.data);
      } catch (err) {
        if (err?.response?.status === 404) {
          toast.error(t("estimates_form.toast_project_deleted"));
          navigate("/app/estimates", { replace: true });
        } else {
          throw err;
        }
      }
    }).catch((err) => {
      if (err?.response?.status === 404) {
        toast.error(t("estimates_form.toast_not_found"));
        navigate("/app/estimates", { replace: true });
      } else {
        toast.error(t("estimates_form.toast_load_failed"));
      }
    });
    api.get("/pricing-profile").then((r) => setProfile(r.data));
  }, [eid, navigate]);

  // Seed the "last checked" snapshot once the estimate loads so an already-
  // ran Double Check isn't shown as stale on first paint. Any subsequent
  // edit to line_items / tax_rate / deposit_pct will differ.
  useEffect(() => {
    if (estimate && estimate.double_check && lastCheckedSnapshot === null) {
      setLastCheckedSnapshot(JSON.stringify({
        li: estimate.line_items,
        tr: estimate.tax_rate,
        dp: estimate.deposit_pct,
      }));
    }
  }, [estimate, lastCheckedSnapshot]);

  // Keep per-document language in sync with header toggle until contractor overrides.
  useEffect(() => { setOutputLang(lang); }, [lang]);

  // iter45 · Edit Polish v1 — beforeunload guard while dirty edits exist.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      // Chrome requires returnValue to be set to trigger the native dialog.
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // iter45 · Edit Polish v1 — fetch the latest proposal for this estimate
  // once on mount so we can show a non-blocking drift banner if the
  // contractor edits + saves the estimate after having already sent a
  // proposal. Uses the existing GET /api/proposals endpoint — no new API,
  // no schema change, no proposal-backend touch.
  useEffect(() => {
    if (!eid) return;
    api.get("/proposals").then(({ data }) => {
      const mine = (data || []).filter((p) => p.estimate_id === eid);
      mine.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setSentProposal(mine[0] || null);
    }).catch(() => { /* non-fatal */ });
  }, [eid]);

  if (!estimate || !project) return <DashboardLayout title={t("pages.estimates.detail_loading_title")} subtitle={t("pages.estimates.detail_loading_subtitle")}><div /></DashboardLayout>;

  // Hardcoded conversion factors the app knows about — keyed as `${qtyUnit}|${basisUnit}` →
  // basis-units per qty-unit. Used by askMaterialBasisChange to skip the prompt for obvious cases.
  const KNOWN_CONVERSIONS = {
    "square|sq ft": 100,
    "sq ft|square": 1 / 100,
    "cubic yard|cubic foot": 27,
    "ton|lb": 2000,
    "lb|ton": 1 / 2000,
  };

  const updateLine = (idx, key, val) => {
    const items = [...estimate.line_items];
    const numKeys = ["quantity", "material_cost", "labor_hours", "labor_cost", "markup_pct", "labor_qty", "labor_unit_cost", "material_qty"];
    const nextLine = { ...items[idx], [key]: numKeys.includes(key) ? parseFloat(val || 0) : val };
    // Slice 2B1 — Approved-pricing quantity edits are job-only overrides.
    // Compare against the initially-displayed prefill value. Any deviation
    // marks the line as contractor-edited so a reviewer can see it was
    // manually overridden. The saved PricingRule is NEVER touched.
    if (key === "quantity" && isInstalledSellingLine(items[idx])) {
      const initial = items[idx].quantity_source_display_value;
      const nextQty = parseFloat(val || 0);
      if (initial !== null && initial !== undefined && !Number.isNaN(parseFloat(initial))) {
        nextLine.quantity_contractor_edited = parseFloat(initial) !== nextQty;
      } else if (nextQty > 0) {
        // No prefill was ever recorded but the contractor entered a value —
        // that IS a contractor-entered quantity.
        nextLine.quantity_contractor_edited = true;
      }
    }
    // When the contractor switches the row's quantity unit, keep unit-based labor basis in sync
    // (e.g., changing unit from "square" → "LF" should keep labor_basis tracking the unit).
    if (key === "unit") {
      const prevUnit = items[idx].unit;
      const prevBasis = items[idx].labor_basis;
      if (prevBasis === prevUnit) nextLine.labor_basis = val;
      // Also keep material_basis in sync if it was tracking the unit, and reset material_qty.
      if (items[idx].material_basis_unit === prevUnit) {
        nextLine.material_basis_unit = val;
        nextLine.material_qty = parseFloat(items[idx].quantity) || 0;
      }
    }
    // When the contractor changes quantity AND basis equals unit, mirror the qty into material_qty.
    if (key === "quantity" && (items[idx].material_basis_unit || items[idx].unit) === items[idx].unit) {
      nextLine.material_qty = parseFloat(val) || 0;
    }
    // Drop "needs pricing" flag when any pricing field changes and the row is now priced.
    if (numKeys.includes(key) && !isLineUnpriced(nextLine)) {
      nextLine.needs_pricing = false;
    }
    // Drop "needs material pricing" flag once the row has a real material total.
    if ((key === "material_cost" || key === "material_qty") && lineMaterialTotal(nextLine) > 0) {
      nextLine.needs_material_pricing = false;
    }
    items[idx] = nextLine;
    setEstimate({ ...estimate, line_items: items });
    markDirty();
  };

  // Apply a multi-field patch to a single line — used by the "Insert from
  // Price Book" flow. Fields are copied as-is (no numeric coercion) because
  // the caller (PriceBookPickerModal) has already produced correctly-typed
  // values. ``needs_pricing`` / ``needs_material_pricing`` flags are cleared
  // when the patch supplies a real material total.
  const patchLine = (idx, patch) => {
    setEstimate((prev) => {
      const items = [...prev.line_items];
      const next = { ...items[idx], ...patch };
      if (lineMaterialTotal(next) > 0) {
        next.needs_material_pricing = false;
      }
      if (!isLineUnpriced(next)) {
        next.needs_pricing = false;
      }
      items[idx] = next;
      return { ...prev, line_items: items };
    });
    markDirty();
  };

  // Triggered when the contractor changes material_basis_unit. If the new basis differs from the
  // quantity unit AND we don't have a built-in conversion, prompt the contractor for one.
  const onMaterialBasisChange = (idx, newBasis) => {
    const row = estimate.line_items[idx];
    const qtyUnit = row.unit || "each";
    if (newBasis === qtyUnit) {
      // Same as qty unit — just mirror quantity → material_qty.
      const items = [...estimate.line_items];
      items[idx] = { ...row, material_basis_unit: newBasis, material_qty: parseFloat(row.quantity) || 0 };
      setEstimate({ ...estimate, line_items: items });
      markDirty();
      return;
    }
    const known = KNOWN_CONVERSIONS[`${qtyUnit}|${newBasis}`];
    if (known !== undefined) {
      const items = [...estimate.line_items];
      items[idx] = {
        ...row,
        material_basis_unit: newBasis,
        material_qty: parseFloat((parseFloat(row.quantity) || 0) * known).toFixed(4) * 1,
      };
      setEstimate({ ...estimate, line_items: items });
      markDirty();
      return;
    }
    setBasisPrompt({ idx, qtyUnit, newBasis, qty: row.quantity });
  };

  const applyBasisConversion = (factor) => {
    if (!basisPrompt) return;
    const { idx, newBasis, qty } = basisPrompt;
    const f = parseFloat(factor) || 0;
    if (f <= 0) { setBasisPrompt(null); return; }
    const items = [...estimate.line_items];
    items[idx] = {
      ...items[idx],
      material_basis_unit: newBasis,
      material_qty: parseFloat(((parseFloat(qty) || 0) * f).toFixed(4)),
    };
    setEstimate({ ...estimate, line_items: items });
    setBasisPrompt(null);
    markDirty();
  };
  const addLine = () => { setEstimate({ ...estimate, line_items: [...estimate.line_items, newLine()] }); markDirty(); };
  // iter45 · Edit Polish v1 — deleting a line offers a 5-second Undo via
  // sonner's action toast. The deleted row + its original index are captured
  // in the toast closure so React state stays clean.
  const removeLine = (idx) => {
    const items = [...estimate.line_items];
    const [removed] = items.splice(idx, 1);
    setEstimate({ ...estimate, line_items: items });
    markDirty();
    toast(t("estimates_form.toast_line_removed", removed?.name), {
      duration: 5000,
      action: {
        label: t("estimates_form.toast_undo"),
        onClick: () => {
          setEstimate((prev) => {
            if (!prev) return prev;
            const restored = [...prev.line_items];
            restored.splice(idx, 0, removed);
            return { ...prev, line_items: restored };
          });
          markDirty();
        },
      },
    });
  };
  // iter45 · Edit Polish v1 — duplicate an existing row. New id, everything
  // else copied; inserted directly below the source row.
  const duplicateLine = (idx) => {
    setEstimate((prev) => {
      if (!prev) return prev;
      const items = [...prev.line_items];
      const src = items[idx];
      if (!src) return prev;
      const copy = { ...src, id: crypto.randomUUID() };
      items.splice(idx + 1, 0, copy);
      return { ...prev, line_items: items };
    });
    markDirty();
  };
  // iter45 · Edit Polish v1 — up/down reorder. Hidden at extremes by the
  // LineItemCard so contractors never try to move past the ends.
  const moveLineUp = (idx) => {
    if (idx <= 0) return;
    setEstimate((prev) => {
      if (!prev) return prev;
      const items = [...prev.line_items];
      [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]];
      return { ...prev, line_items: items };
    });
    markDirty();
  };
  const moveLineDown = (idx) => {
    setEstimate((prev) => {
      if (!prev) return prev;
      if (idx < 0 || idx >= prev.line_items.length - 1) return prev;
      const items = [...prev.line_items];
      [items[idx], items[idx + 1]] = [items[idx + 1], items[idx]];
      return { ...prev, line_items: items };
    });
    markDirty();
  };

  const save = async (opts = {}) => {
    const { silent = false } = opts;
    setRunning((r) => ({ ...r, save: true }));
    try {
      const { data } = await api.put(`/estimates/${eid}`, {
        project_id: estimate.project_id,
        line_items: estimate.line_items,
        tax_rate: estimate.tax_rate,
        deposit_pct: estimate.deposit_pct,
        notes: estimate.notes,
        ai_scope: estimate.ai_scope,
        ai_check: estimate.ai_check,
        // Roofing Simple Builder MVP (R1 + R2) — additive estimate metadata
        // persisted on every save path.
        trade: estimate.trade ?? null,
        roof_system_type: estimate.roof_system_type ?? null,
        estimate_pricing_style: estimate.estimate_pricing_style ?? null,
        detailed_mode_open: estimate.detailed_mode_open ?? null,
      });
      setEstimate(data);
      setDirty(false);
      // iter45 · after save, refresh the proposal-drift signal.
      try {
        const { data: proposals } = await api.get("/proposals");
        const mine = (proposals || []).filter((p) => p.estimate_id === eid);
        mine.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        setSentProposal(mine[0] || null);
      } catch { /* non-fatal */ }
      if (!silent) toast.success(t("estimates_form.toast_saved"));
    } catch (err) {
      // iter57 · Slice 1B — hard 409 document_locked. Steer the
      // contractor into the /new-version flow so the sent proposal
      // stays byte-identical.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.error === "document_locked") {
        setLockedBanner(detail);
        if (!silent) toast.error(t("estimates_form.toast_locked"));
        else throw new Error("estimate_locked");
      } else if (!silent) {
        toast.error(t("estimates_form.toast_save_failed"));
      } else {
        throw new Error("estimate_save_failed");
      }
    } finally {
      setRunning((r) => ({ ...r, save: false }));
    }
  };

  // iter57 · Slice 1B — Contractor-friendly revision path. Clones the
  // locked estimate into a fresh draft (v2/v3/...) preserving the Job,
  // Customer, Property, trade, system, scope, quantities, pricing, and
  // internal notes. Sends no customer message, shares nothing.
  const createNewVersion = async () => {
    setRunning((r) => ({ ...r, newVersion: true }));
    try {
      const { data: v } = await api.post(`/estimates/${eid}/new-version`);
      toast.success(t("estimates_form.toast_new_version", v.estimate_version));
      setLockedBanner(null);
      navigate(`/app/estimates/${v.id}`);
    } catch {
      toast.error(t("estimates_form.toast_new_version_failed"));
    } finally {
      setRunning((r) => ({ ...r, newVersion: false }));
    }
  };

  // Slice 2B1 (Approved Pricing Precedence) — Duplicate suppression
  // helper. Given the current estimate's line_items, returns:
  //   { tokens, suppressCleanup, aliasMap }
  // that ``organizeWithAI`` uses to drop AI-suggested rows which would
  // duplicate a scope already covered by an auto-staged approved
  // reusable PricingRule. Only rows generated for the SAME matched
  // scope are dropped — unrelated scope (baseboards, transitions the
  // AI names distinctly, etc.) still passes through.
  const buildApprovedScopeSuppression = (items) => {
    // Small trade→canonical-name→shortcut-aliases table. Registry keywords
    // live server-side; the AI, however, routinely emits shortcut labels
    // (``LVP``, ``hardie``) that the canonical name alone does not cover.
    // Keeping this table narrow and additive avoids over-suppression.
    const ALIASES = {
      "Luxury Vinyl Plank / Engineered Flooring": ["lvp", "luxury vinyl", "vinyl plank", "engineered flooring"],
      "Hardwood Flooring":                        ["hardwood", "solid wood"],
      "Tile Flooring":                            ["tile floor", "ceramic floor", "porcelain floor"],
      "Carpet":                                   ["carpet"],
      "Flooring Repair / Replacement":            ["flooring repair", "floor repair", "replace plank"],
      "Interior Repaint":                         ["interior paint", "interior repaint", "wall paint", "room repaint"],
      "Exterior Repaint":                         ["exterior paint", "exterior repaint", "house paint", "siding paint"],
      "Drywall Repair / Patch":                   ["drywall repair", "drywall patch", "wall patch", "sheetrock patch"],
      "Hang and Finish":                          ["hang and finish", "hang drywall", "tape and mud"],
      "Vinyl Siding":                             ["vinyl siding"],
      "Fiber Cement Siding":                      ["fiber cement", "hardie"],
    };
    const tokens = new Set();
    let suppressCleanup = false;
    const suppressedTrades = new Set();
    for (const li of items || []) {
      if (!li) continue;
      // The stable signal that a line came from an approved reusable
      // PricingRule staged via the /auto-stage[/{trade}] endpoints is
      // the presence of ``pricing_rule_id``. ``auto_staged`` /
      // ``is_installed_selling`` are additional hints, but the estimate
      // PUT round-trip normalises some transient flags off the shape —
      // pricing_rule_id survives because it links back to the approved
      // rule and is required by validation.
      const hasApprovedRule = Boolean(li.pricing_rule_id);
      if (!hasApprovedRule) continue;
      const isInstalledSelling = (
        li.is_installed_selling === true
        || li.pricing_type === "installed_selling"
        || li.auto_staged === true
      );
      if (isInstalledSelling) suppressCleanup = true;
      if (li.auto_stage_trade) suppressedTrades.add((li.auto_stage_trade || "").toLowerCase());
      const name = (li.name || "").toLowerCase();
      // Every word ≥4 chars from the canonical system label becomes a
      // suppression token (e.g. ``luxury``, ``vinyl``, ``plank``,
      // ``engineered``, ``flooring``).
      name.split(/[^a-z0-9]+/).filter((w) => w.length >= 4).forEach((w) => tokens.add(w));
      // Shortcut aliases the AI is known to emit for that system.
      (ALIASES[li.name] || []).forEach((a) => tokens.add(a.toLowerCase()));
    }
    return { tokens: Array.from(tokens), suppressCleanup, suppressedTrades: Array.from(suppressedTrades) };
  };

  const shouldSuppressSuggestion = (s, sup) => {
    if (!sup) return false;
    if (!sup.tokens.length && !sup.suppressCleanup) return false;
    const label = ((s?.name || "") + " " + (s?.description || "")).toLowerCase();
    if (!label.trim()) return false;
    // (1) Direct scope duplicate — any suppression token appears in the
    //     suggestion's name/description.
    if (sup.tokens.some((tok) => label.indexOf(tok) !== -1)) return true;
    // (2) Cleanup / haul-off / disposal / dumpster suggestion when an
    //     approved installed-selling rule is already staged (installed-
    //     selling is contractually all-in, cleanup included).
    if (sup.suppressCleanup) {
      if (/\b(clean.?up|final.?clean|final.?cleaning|debris|haul.?off|hauling|dispose|disposal|dumpster.?fee|jobsite.?clean)\b/.test(label)) {
        return true;
      }
    }
    return false;
  };

  const organizeWithAI = async () => {
    // iter49 · Pre-flight — refuse to send the AI an empty prompt. Better
    // UX than a confusing "0 line items" result after a 30s round-trip.
    const notesPresent =
      (project?.voice_transcript || "").trim() ||
      (project?.typed_notes || "").trim() ||
      (project?.job_description || "").trim() ||
      (project?.measurements || "").trim() ||
      (project?.material_notes || "").trim() ||
      (project?.special_conditions || "").trim();
    if (!notesPresent) {
      toast.error(t("estimates_form.toast_no_walkthrough"), { duration: 7000 });
      return;
    }
    setRunning((r) => ({ ...r, scope: true }));
    try {
      const { data } = await api.post("/ai/organize-scope", { project_id: estimate.project_id, language: outputLang });
      const suggested = data.suggested_line_items || [];
      // Approved Pricing Precedence — drop suggestions that duplicate a
      // scope already covered by an auto-staged approved reusable
      // PricingRule. Unrelated scope survives untouched.
      const sup = buildApprovedScopeSuppression(estimate.line_items);
      const keptSuggestions = suggested.filter((s) => !shouldSuppressSuggestion(s, sup));
      const suppressedCount = suggested.length - keptSuggestions.length;
      const lineItems = keptSuggestions.map((s) => ({
        ...newLine(),
        ...s,
        quantity: s.quantity || 1,
        unit: s.unit || "each",
        material_basis_unit: s.material_basis_unit || s.unit || "each",
        material_qty: s.material_qty ?? s.quantity ?? 1,
        labor_basis: (s.labor_basis || "hour"),
        labor_qty: s.labor_qty ?? 0,
        labor_unit_cost: s.labor_unit_cost ?? 0,
        material_required: s.material_required === true,
        needs_material_pricing: s.needs_material_pricing === true || (s.material_required === true && (!s.material_cost || s.material_cost <= 0)),
        needs_pricing: s.needs_pricing === true || isLineUnpriced(s),
      }));
      const mergedItems = [...estimate.line_items, ...lineItems];
      // Persist and let the backend recompute totals — then sync local state from the response
      // so the sidebar reflects the seeded labor/material pricing immediately.
      const { data: saved } = await api.put(`/estimates/${eid}`, {
        project_id: estimate.project_id,
        line_items: mergedItems,
        tax_rate: estimate.tax_rate,
        deposit_pct: estimate.deposit_pct,
        notes: estimate.notes,
        ai_scope: data,
        ai_check: estimate.ai_check,
        double_check: estimate.double_check,
        double_check_ack: estimate.double_check_ack,
        trade: estimate.trade ?? null,
        roof_system_type: estimate.roof_system_type ?? null,
        estimate_pricing_style: estimate.estimate_pricing_style ?? null,
        detailed_mode_open: estimate.detailed_mode_open ?? null,
      });
      setEstimate(saved);
      setDirty(false);
      setScopeBanner({ added: lineItems.length, suppressed: suppressedCount });
      if (lineItems.length === 0 && suppressedCount === 0) {
        // iter49 · surface the "AI ran but returned nothing" case as an
        // error instead of a silent success — the contractor's next step
        // is to add walkthrough detail on the project page.
        toast.error(t("estimates_form.toast_organize_empty"), { duration: 7000 });
      } else if (lineItems.length === 0 && suppressedCount > 0) {
        // All suggestions duplicated a scope already covered by an
        // auto-staged approved-pricing line — nothing left to add.
        toast.success(t("estimates_form.toast_organize_all_covered") || "Scope already covered by your approved pricing — nothing new to add.");
      } else {
        toast.success(t("estimates_form.toast_organize_added", lineItems.length));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("estimates_form.toast_ai_scope_failed"));
    } finally {
      setRunning((r) => ({ ...r, scope: false }));
    }
  };

  const runCheck = async () => {
    setRunning((r) => ({ ...r, check: true }));
    try {
      // save first so AI sees latest
      const { data: savedEst } = await api.put(`/estimates/${eid}`, {
        project_id: estimate.project_id,
        line_items: estimate.line_items,
        tax_rate: estimate.tax_rate,
        deposit_pct: estimate.deposit_pct,
        notes: estimate.notes,
        ai_scope: estimate.ai_scope,
        ai_check: estimate.ai_check,
        double_check: estimate.double_check,
        double_check_ack: estimate.double_check_ack,
        // Roofing Simple Builder MVP (R1 + R2) — persist estimate-level
        // metadata on every save path so AI / Double Check / manual PUTs
        // never wipe the contractor's roof-system + pricing-style picks.
        trade: estimate.trade ?? null,
        roof_system_type: estimate.roof_system_type ?? null,
        estimate_pricing_style: estimate.estimate_pricing_style ?? null,
        detailed_mode_open: estimate.detailed_mode_open ?? null,
      });
      const { data } = await api.post("/ai/estimate-check", { estimate_id: eid });
      setEstimate({ ...savedEst, ai_check: data });
      setDirty(false);
      toast.success(t("estimates_form.toast_ai_check_done"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("estimates_form.toast_ai_check_failed"));
    } finally {
      setRunning((r) => ({ ...r, check: false }));
    }
  };

  // Slice A3.1 — Deterministic Double Check. Advisory only; never blocks the
  // proposal button. Saves the estimate first so the backend runs the checks
  // against the same shape the contractor sees, then persists the payload on
  // ``estimate.double_check``.
  const runDoubleCheck = async () => {
    setRunningDoubleCheck(true);
    try {
      const { data: savedEst } = await api.put(`/estimates/${eid}`, {
        project_id: estimate.project_id,
        line_items: estimate.line_items,
        tax_rate: estimate.tax_rate,
        deposit_pct: estimate.deposit_pct,
        notes: estimate.notes,
        ai_scope: estimate.ai_scope,
        ai_check: estimate.ai_check,
        double_check: estimate.double_check,
        double_check_ack: estimate.double_check_ack,
        // Roofing Simple Builder MVP (R1 + R2) — persist estimate-level
        // metadata on every save path so AI / Double Check / manual PUTs
        // never wipe the contractor's roof-system + pricing-style picks.
        trade: estimate.trade ?? null,
        roof_system_type: estimate.roof_system_type ?? null,
        estimate_pricing_style: estimate.estimate_pricing_style ?? null,
        detailed_mode_open: estimate.detailed_mode_open ?? null,
      });
      const { data } = await api.post(`/estimates/${eid}/double-check`, {
        // Slice 2J correction §9 · Option 2 — AI advisory is kept
        // INTERNAL AND OFF. The frontend always sends include_ai=false
        // so only the deterministic engine runs. The underlying
        // capability remains in place in backend/server.py for a
        // later Product Owner decision.
        include_ai: false,
      });
      setEstimate({ ...savedEst, double_check: data });
      setDirty(false);
      setLastCheckedSnapshot(JSON.stringify({
        li: savedEst.line_items,
        tr: savedEst.tax_rate,
        dp: savedEst.deposit_pct,
      }));
      const sev = data.overall_severity;
      if (sev === "red") toast.error(t("estimates_form.toast_dc_red", data.red_count));
      else if (sev === "yellow") toast.warning(t("estimates_form.toast_dc_yellow", data.yellow_count));
      else toast.success(t("estimates_form.toast_dc_green"));
      // iter34 — auto-scroll to the findings panel when there is something
      // worth reviewing. Green runs stay where they are (no need to move
      // the contractor's viewport for a clean result).
      if (sev === "red" || sev === "yellow") {
        scrollToDoubleCheckPanel();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("estimates_form.toast_dc_failed"));
    } finally {
      setRunningDoubleCheck(false);
    }
  };

  // Derived — "stale" when the estimate was edited after the last Double Check run.
  const currentEstimateSnapshot = JSON.stringify({
    li: estimate.line_items,
    tr: estimate.tax_rate,
    dp: estimate.deposit_pct,
  });
  const isDoubleCheckStale = Boolean(
    estimate.double_check && lastCheckedSnapshot && currentEstimateSnapshot !== lastCheckedSnapshot,
  );

  // Build a list of human-readable problems that should block (or warn before)
  // sending a proposal — used by both the Generate Proposal guard and the
  // hard-coded red finding inside the AI Estimate Check panel.
  const buildProposalIssues = () => {
    const issues = [];
    const items = estimate?.line_items || [];
    const priced = items.filter((l) => !isLineUnpriced(l) && !isLineNeedsQuantity(l));
    if (items.length === 0) {
      issues.push(t("estimates_form.issue_no_items"));
    }
    if (priced.length === 0 && items.length > 0) {
      issues.push(t("estimates_form.issue_none_ready"));
    }
    const unpriced = items.filter((l) => isLineUnpriced(l));
    if (priced.length > 0 && unpriced.length > 0) {
      issues.push(t("estimates_form.issue_unpriced", unpriced.length));
    }
    // Slice 2B1 — Approved-pricing lines missing a quantity are a
    // proposal blocker with a QUANTITY reason (never a pricing reason).
    const needsQty = items.filter((l) => isLineNeedsQuantity(l));
    if (needsQty.length > 0) {
      issues.push(t("estimates_form.issue_needs_qty", needsQty.length));
    }
    // Lines that require material but have material_cost = $0 — major safety gap.
    const needMat = items.filter((l) => isLineNeedsMaterial(l));
    if (needMat.length > 0) {
      issues.push(t("estimates_form.issue_needs_material", needMat.length));
    }
    const missingName = items.filter((l) => !((l.name || "").trim()));
    if (missingName.length > 0) {
      issues.push(t("estimates_form.issue_missing_name", missingName.length));
    }
    if ((estimate?.total || 0) <= 0 && priced.length > 0 && needsQty.length === 0) {
      issues.push(t("estimates_form.issue_total_zero"));
    }
    return issues;
  };

  // Counters used by inline banners on the estimate page.
  const incompleteCounts = () => {
    const items = estimate?.line_items || [];
    return {
      unpriced: items.filter((l) => isLineUnpriced(l)).length,
      needMaterial: items.filter((l) => isLineNeedsMaterial(l)).length,
      // Slice 2B1 — Approved-pricing rows with a missing / zero quantity.
      // Distinct from `unpriced` so banners can show the right copy.
      needsQuantity: items.filter((l) => isLineNeedsQuantity(l)).length,
      total: items.length,
    };
  };

  const handleGenerateProposalClick = async () => {
    // iter47 · Pre-flight: block if any saved document / closeout default
    // still contains an unresolved bracket placeholder like `[Company]`,
    // `[X days]`, `[X years]`, or `[Edit …]`. Contractor is directed to
    // fix it in Document Basics before a proposal PDF is generated.
    try {
      const [dd, cd] = await Promise.all([
        api.get("/document-defaults").catch(() => null),
        api.get("/closeout-defaults").catch(() => null),
      ]);
      const bag = { ...(dd?.data || {}), ...(cd?.data || {}) };
      const bad = findFieldsWithUnresolvedPlaceholders(bag);
      if (bad.length > 0) {
        toast.error(
          t("estimates_form.issue_placeholders", bad.join(", ")),
          { duration: 8000 },
        );
        return;
      }
    } catch { /* non-fatal — fall through */ }

    const issues = buildProposalIssues();
    if (issues.length > 0) {
      // iter55b · If the local pre-flight already knows the backend will
      // hard-block (unpriced rows, $0 total, no items, required material
      // missing), hide "Send anyway" up front. Any other soft issue
      // (e.g. missing line item names, sales-tax hint) is allowed to
      // pass through with the confirm button.
      const items = estimate?.line_items || [];
      const hasUnpriced = items.some((l) => isLineUnpriced(l));
      const hasNeedsMaterial = items.some((l) => isLineNeedsMaterial(l));
      const noPriced = items.filter((l) => !isLineUnpriced(l)).length === 0;
      const hardBlock = items.length === 0 || noPriced || hasUnpriced || hasNeedsMaterial;
      setProposalGuard({
        issues,
        hardBlock,
        headline: hardBlock
          ? t("estimates_form.guard_hard_headline")
          : t("estimates_form.guard_soft_headline"),
      });
      return;
    }
    sendProposal();
  };

  const sendProposal = async (opts = {}) => {
    const { skipSave = false } = opts;
    setSendingProposal(true);
    try {
      // save first (unless we're retrying immediately after an ack — the
      // save PUT would otherwise overwrite the fresh double_check_ack
      // captured in this render's stale closure).
      // iter55b · Silent save inside the proposal flow so a subsequent
      // hard-block 409 never leaves the "Estimate saved" toast on
      // screen implying success.
      if (!skipSave) {
        try {
          await save({ silent: true });
        } catch {
          toast.error(t("estimates_form.toast_save_before_send"));
          setSendingProposal(false);
          return;
        }
      }
      const { data } = await api.post("/proposals", {
        estimate_id: eid,
        included_items: estimate.ai_scope?.organized_scope || [],
        excluded_items: estimate.ai_scope?.risk_areas || [],
        payment_schedule: profile?.payment_terms || "",
        timeline: project?.timeline || "",
        next_steps: "",
        language: outputLang,
      });
      toast.success(t("estimates_form.toast_proposal_generated"));
      setProposalGuard(null);
      setAckDialog(null);
      navigate(`/app/proposals/${data.id}`);
    } catch (err) {
      // Slice A3.2 — Soft gate. If backend returns 409 with
      // double_check_required, open the acknowledgement dialog with the
      // fresh Double Check payload the server just recomputed.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.error === "double_check_required") {
        setEstimate((prev) => prev ? { ...prev, double_check: detail.double_check } : prev);
        setAckDialog({ doubleCheck: detail.double_check });
        setAckConfirmed(false);
        setProposalGuard(null);
      } else if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.error === "estimate_pricing_incomplete") {
        // iter55b · Backend refused. Re-open the guard in HARD-BLOCK mode
        // so no "Send anyway" is offered. Surface the backend-authoritative
        // reasons + the fixed failure headline. Never fire a success toast.
        setProposalGuard({
          hardBlock: true,
          headline: t("estimates_form.issue_proposal_not_created"),
          issues: detail.reasons || [t("estimates_form.issue_hardblock_fallback")],
        });
      } else {
        toast.error(t("estimates_form.toast_proposal_failed"));
      }
    } finally {
      setSendingProposal(false);
    }
  };

  // Slice A3.2 — On ack confirmation, POST the acknowledgement then retry
  // the proposal create. If the estimate has drifted since the dialog
  // opened, the ack endpoint will return 409 itself; we surface that.
  const acknowledgeAndProceed = async () => {
    if (!ackDialog?.doubleCheck?.estimate_hash) return;
    setAcknowledging(true);
    try {
      const { data: ack } = await api.post(`/estimates/${eid}/ack-double-check`, {
        estimate_hash: ackDialog.doubleCheck.estimate_hash,
      });
      setEstimate((prev) => prev ? { ...prev, double_check_ack: ack } : prev);
      // Retry proposal creation now that ack is stamped. Skip the save PUT
      // because it would race with the setEstimate above and re-null the
      // double_check_ack the server just accepted.
      await sendProposal({ skipSave: true });
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error(t("estimates_form.toast_ack_stale"));
        setAckDialog(null);
      } else {
        toast.error(t("estimates_form.toast_ack_failed"));
      }
    } finally {
      setAcknowledging(false);
    }
  };

  // Live sidebar preview — mirrors backend compute_estimate_totals so the
  // right-hand Totals box updates the moment a contractor types qty/rate on
  // any line (Slice F fixed-price mode OR standard material+labor+markup).
  // The server remains the source of truth on Save Estimate; this preview
  // just kills the "did anything happen?" hesitation between edit and save.
  const livePreview = (() => {
    const items = estimate?.line_items || [];
    let subtotal = 0;
    for (const li of items) {
      subtotal += (Number(lineEffectiveTotal(li)) || 0);
    }
    const taxRate = parseFloat(estimate?.tax_rate ?? 0) || 0;
    const depositPct = parseFloat(estimate?.deposit_pct ?? 0) || 0;
    const tax = Math.round(subtotal * (taxRate / 100) * 100) / 100;
    const total = Math.round((subtotal + tax) * 100) / 100;
    const deposit = Math.round(total * (depositPct / 100) * 100) / 100;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      tax,
      total,
      deposit,
      balance_due: Math.round((total - deposit) * 100) / 100,
    };
  })();

  return (
    <DashboardLayout
      title={`${t("estimates_form.detail_title_prefix")} · ${project.customer_name}`}
      subtitle={
        // Rio dry-run finding #7 (iter32) — make the estimate subtitle a
        // discoverable path back to the project intake / voice notes area
        // when a project_id is known. Display copy is unchanged; the wrapper
        // is only added when the destination is known.
        project?.id ? (
          <a
            href={`/app/projects/${project.id}`}
            onClick={(e) => { e.preventDefault(); navigate(`/app/projects/${project.id}`); }}
            data-testid="estimate-subtitle-back-to-job"
            className="hover:text-[var(--jc-text)] hover:underline decoration-dotted underline-offset-4"
            title={t("estimates_form.subtitle_back_title")}
          >
            {`${project.job_type || "—"} · ${project.job_address || ""}`}
          </a>
        ) : (
          `${project.job_type || "—"} · ${project.job_address || ""}`
        )
      }
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={() => project?.id ? navigate(`/app/projects/${project.id}`) : navigate(-1)}
            data-testid={project?.id ? "estimate-back-to-job-btn" : "estimate-back-btn"}
            title={project?.id ? t("estimates_form.back_to_job_title") : t("estimates_form.back_short")}
            className="text-sm font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] inline-flex items-center gap-1"
          >
            <ArrowLeft size={14} /> {project?.id ? t("estimates_form.back_to_job") : t("estimates_form.back_short")}
          </button>
          {/* Slice 2J amendment · Visual separator so Back-to-job
              (navigation) is clearly distinct from the action group
              on the right. */}
          <span
            aria-hidden
            data-testid="header-actions-divider"
            className="mx-3 hidden sm:inline-block h-6 w-px bg-[var(--jc-border-strong)]"
          />
          <div className="hidden sm:inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-[var(--jc-border-strong)] text-xs" data-testid="estimate-output-lang">
            <Languages size={13} className="text-[var(--jc-text-soft)]" />
            <span className="text-[var(--jc-text-soft)]">{t("estimates_form.customer_output_label")}</span>
            <select
              value={outputLang}
              onChange={(e) => setOutputLang(e.target.value)}
              data-testid="estimate-output-lang-select"
              className="ml-1 bg-transparent font-semibold focus:outline-none"
            >
              <option value="en">{t("estimates_form.lang_english")}</option>
              <option value="es">{t("estimates_form.lang_spanish")}</option>
            </select>
          </div>
          {/* Slice 2J amendment · Edit Estimate is now grouped with
              the estimate actions (Customer output · Edit · Save ·
              Generate Proposal) — visually distinct from the Back
              navigation on the left. */}
          {project?.id && estimate?.id && (
            <button
              onClick={() => navigate(`/app/estimates/new?project=${project.id}&edit_estimate=${estimate.id}`)}
              data-testid="estimate-edit-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
            >
              {t("estimates_form.edit_estimate_btn")}
            </button>
          )}
          <button
            onClick={save}
            disabled={running.save}
            data-testid="estimate-save-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md ${
              dirty
                ? "jc-btn-primary"
                : "border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
            }`}
          >
            <Save size={14} /> {running.save ? t("estimates_form.saving") : dirty ? t("estimates_form.save_changes") : t("estimates_form.save")}
            {dirty && <span data-testid="estimate-save-btn-dot" className="ml-0.5 inline-block w-1.5 h-1.5 rounded-full bg-yellow-300" aria-hidden />}
          </button>
          <button onClick={handleGenerateProposalClick} data-testid="estimate-send-proposal-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary">
            <Send size={14} /> {t("estimates_form.generate_proposal")}
          </button>
        </div>
      }
    >
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          {/* iter45 · Edit Polish v1 — meta bar: unsaved indicator + last
              updated timestamp + non-blocking proposal-drift teaser. */}
          <div className="flex items-center justify-between gap-3 text-xs" data-testid="estimate-editor-meta">
            <div className="flex items-center gap-2">
              {dirty ? (
                <span
                  data-testid="estimate-unsaved-banner"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-50 text-yellow-900 border border-yellow-300 font-semibold"
                >
                  <AlertTriangle size={12} /> {t("estimates_form.unsaved_changes")}
                </span>
              ) : (
                <span
                  data-testid="estimate-saved-badge"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold"
                >
                  <ShieldCheck size={12} /> {t("estimates_form.all_saved")}
                </span>
              )}
            </div>
            {estimate?.updated_at && (
              <span
                data-testid="estimate-last-updated"
                title={estimate.updated_at}
                className="inline-flex items-center gap-1 text-[var(--jc-text-soft)]"
              >
                <Clock size={12} /> {t("estimates_form.last_updated", new Date(estimate.updated_at).toLocaleString())}
              </span>
            )}
          </div>
          {sentProposal && estimate?.updated_at && sentProposal.created_at && (estimate.updated_at > sentProposal.created_at) && !dirty && (
            <div
              data-testid="estimate-proposal-drift-banner"
              className="jc-card p-3 border-l-4 border-l-yellow-400 bg-yellow-50/40 flex items-start gap-3"
            >
              <AlertTriangle size={16} className="text-yellow-700 mt-0.5 shrink-0" />
              <div className="flex-1 text-sm text-yellow-900">
                <div className="font-semibold">{t("estimates_form.drift_headline")}</div>
                <div className="text-xs mt-0.5">
                  {t("estimates_form.drift_body")}
                </div>
              </div>
              <button
                onClick={handleGenerateProposalClick}
                data-testid="estimate-proposal-drift-regen-btn"
                className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary"
              >
                <Send size={12} /> {t("estimates_form.regenerate_proposal")}
              </button>
            </div>
          )}
          {/* iter57 · Slice 1B — document_locked banner. Backend returns
              HTTP 409 with {error:"document_locked", reason, source_estimate_id,
              linked_proposal_id, recommended_action} when the estimate
              has a sent/signed/etc proposal linked. The contractor's
              only safe move is to create a new estimate version. */}
          {lockedBanner && (
            <div
              data-testid="estimate-locked-banner"
              className="jc-card p-3 border-l-4 border-l-red-500 bg-red-50/50 flex items-start gap-3"
            >
              <AlertTriangle size={16} className="text-red-700 mt-0.5 shrink-0" />
              <div className="flex-1 text-sm text-red-900">
                <div className="font-semibold">{t("estimates_form.locked_headline")}</div>
                <div className="text-xs mt-0.5">
                  {lockedBanner.message || t("estimates_form.locked_body_default")}
                </div>
              </div>
              <button
                onClick={createNewVersion}
                disabled={running.newVersion}
                data-testid="estimate-create-new-version-btn"
                className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary disabled:opacity-50"
              >
                {running.newVersion ? t("estimates_form.creating_new_version") : t("estimates_form.create_new_version")}
              </button>
            </div>
          )}
          {/* iter54 · P0 Pricing Readiness — Account-level warning banner.
              Advisory only. Distinct from the estimate-level unpriced-rows
              banner (which uses live line_items and blocks proposal send). */}
          {accountReadiness && accountReadiness.pricing_ready === false && (
            <div
              data-testid="estimate-account-pricing-incomplete-banner"
              className="jc-card p-3 border-l-4 border-l-amber-400 bg-amber-50/50 flex items-start gap-3"
            >
              <AlertTriangle size={16} className="text-amber-700 mt-0.5 shrink-0" />
              <div className="flex-1 text-sm text-amber-900">
                <div className="font-semibold">{t("estimates_form.account_pricing_headline")}</div>
                <div className="text-xs mt-1">
                  {t("estimates_form.account_pricing_body")}
                </div>
                {Array.isArray(accountReadiness.pricing_readiness_reasons) && accountReadiness.pricing_readiness_reasons.length > 0 && (
                  <ul
                    data-testid="estimate-account-pricing-reasons"
                    className="text-xs mt-1.5 list-disc list-inside space-y-0.5"
                  >
                    {accountReadiness.pricing_readiness_reasons.slice(0, 5).map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                )}
              </div>
              <Link
                to="/app/setup#trade-pricing"
                data-testid="estimate-account-pricing-fix-link"
                className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md border border-amber-300 text-amber-900 hover:bg-amber-100"
              >
                {t("estimates_form.complete_pricing_setup")}
              </Link>
            </div>
          )}
          {/* Slice 2B1 (Workstream B) — Multiple approved Roofing systems
              matched the job notes. Contractor selection is required —
              the app must not silently pick one. */}
          {autoStageMultiple && autoStageMultiple.length > 0 && (
            <div
              data-testid="auto-stage-multiple-matches-panel"
              className="jc-card p-3 border-l-4 border-l-sky-500 bg-sky-50/60"
            >
              <div className="text-sm font-semibold text-sky-900">
                {t("estimates_form.multi_headline")}
              </div>
              <div className="text-[11px] text-sky-800 mt-1"
                   data-testid="auto-stage-multi-installed-copy">
                <b>{t("estimates_form.multi_installed_prefix")}</b> {t("estimates_form.multi_installed_copy")}
              </div>
              <div className="mt-2 space-y-2">
                {autoStageMultiple.map((c) => (
                  <div key={c.pricing_rule_id}
                       data-testid={`auto-stage-candidate-${c.pricing_rule_id}`}
                       className="flex items-center justify-between gap-2 rounded border border-sky-200 bg-white p-2">
                    <div>
                      <div className="text-sm font-semibold">{c.system_type}</div>
                      <div className="text-[11px] text-[var(--jc-text-soft)]">
                        {t("estimates_form.multi_price_per", Number(c.selling_price_per_unit || 0).toFixed(2), c.selling_price_unit)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const r = await api.post(
                            `/onboarding/trade-setup/estimates/${eid}/auto-stage-roofing`,
                            { pricing_rule_id: c.pricing_rule_id },
                          );
                          const body = r.data || {};
                          if (body.result === "auto_staged" && body.line) {
                            setEstimate((prev) => {
                              const items = prev?.line_items || [];
                              if (items.some((li) => li && li.pricing_rule_id === c.pricing_rule_id)) return prev;
                              return { ...prev, line_items: [...items, body.line] };
                            });
                          }
                          setAutoStageMultiple(null);
                          toast.success(t("estimates_form.toast_staged", c.system_type));
                        } catch (e) {
                          toast.error(t("estimates_form.toast_stage_failed"));
                        }
                      }}
                      className="text-xs px-2 py-1 rounded jc-btn-primary"
                      data-testid={`auto-stage-select-${c.pricing_rule_id}`}
                    >
                      {t("estimates_form.multi_use_system")}
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setAutoStageMultiple(null)}
                  className="text-[11px] text-[var(--jc-text-soft)] underline"
                  data-testid="auto-stage-dismiss-multi"
                >
                  {t("estimates_form.multi_dismiss")}
                </button>
              </div>
            </div>
          )}
          {/* iter54 · Estimate-level unpriced-rows warning. Distinct copy
              from the account-level banner so contractors can tell them
              apart at a glance. Slice 2B1: approved-pricing lines missing
              a quantity get their OWN dedicated banner (below) — never
              lumped into "unpriced" / "material pricing incomplete". */}
          {(() => {
            const counts = incompleteCounts();
            if (counts.total === 0) return null;
            if (counts.unpriced === 0 && counts.needMaterial === 0) return null;
            return (
              <div
                data-testid="estimate-unpriced-rows-banner"
                className="jc-card p-3 border-l-4 border-l-rose-500 bg-rose-50/60 flex items-start gap-3"
              >
                <AlertTriangle size={16} className="text-rose-700 mt-0.5 shrink-0" />
                <div className="flex-1 text-sm text-rose-900">
                  <div className="font-semibold">{t("estimates_form.unpriced_headline")}</div>
                  <div className="text-xs mt-1">
                    {counts.unpriced > 0 && (
                      <span>{t("estimates_form.unpriced_lines", counts.unpriced)}</span>
                    )}
                    {counts.needMaterial > 0 && (
                      <span>{t("estimates_form.unpriced_need_material", counts.needMaterial)}</span>
                    )}
                    <span>{t("estimates_form.unpriced_add_prices")}</span>
                  </div>
                </div>
              </div>
            );
          })()}
          {/* Slice 2B1 · Approved-pricing lines are separately blocked on
              a missing quantity. The price IS approved — only the quantity
              is missing. Copy avoids "needs pricing" / "material pricing
              incomplete" language entirely. */}
          {(() => {
            const counts = incompleteCounts();
            if (counts.needsQuantity <= 0) return null;
            return (
              <div
                data-testid="estimate-needs-quantity-banner"
                className="jc-card p-3 border-l-4 border-l-amber-500 bg-amber-50/60 flex items-start gap-3"
              >
                <AlertTriangle size={16} className="text-amber-700 mt-0.5 shrink-0" />
                <div className="flex-1 text-sm text-amber-900">
                  <div className="font-semibold">
                    {t("estimates_form.needs_qty_headline", counts.needsQuantity)}
                  </div>
                  <div className="text-xs mt-1">
                    {t("estimates_form.needs_qty_body")}
                  </div>
                </div>
              </div>
            );
          })()}
          <RoofingSimpleBuilder
            project={project}
            estimate={estimate}
            setEstimate={setEstimate}
            addLine={addLine}
            insertLine={(preset, opts = {}) => {
              // Slice 2B1 — atomic dedupe inside the functional updater.
              // The updater is the single source of truth for the outcome:
              // it either appends the new line (inserted) or refuses
              // re-insertion (duplicate). Toast decisions are dispatched
              // from HERE so they cannot mis-fire against a stale-closure
              // snapshot of `estimate.line_items` in the caller.
              let outcome = "inserted";
              setEstimate((prev) => {
                const items = prev?.line_items || [];
                if (preset?.id && items.some((li) => li && li.id === preset.id)) {
                  outcome = "duplicate";
                  return prev;
                }
                if (preset?.pricing_rule_id && items.some((li) => li && li.pricing_rule_id === preset.pricing_rule_id)) {
                  outcome = "duplicate";
                  return prev;
                }
                return { ...prev, line_items: [...items, { ...newLine(), ...preset }] };
              });
              if (outcome === "duplicate") {
                opts.onDuplicate?.();
              } else {
                opts.onInserted?.();
                markDirty();
              }
            }}
          />
          {/* Slice 2J · Ask Once, Reuse Everywhere.
              Compact contractor-controlled cue. Offered only when the
              estimate's own notes are empty and the project intake has
              a reusable summary. Never mutates the project; never
              overwrites populated / contractor-edited estimate notes;
              dismissal leaves notes unchanged. */}
          <EstimateNotesReuseCue
            estimate={estimate}
            project={project}
            onPrefill={(text) => {
              setEstimate((prev) => (
                // Guard against a race where notes were populated between
                // render and click — never overwrite non-empty notes.
                ((prev?.notes || "").trim())
                  ? prev
                  : { ...prev, notes: text }
              ));
              markDirty();
            }}
            t={t}
          />

          {/* Slice 2I · Levi Unified Estimate Experience — one primary
              contractor-facing AI surface. Presents Levi as the single AI
              coworker; the Organize Scope engine and AI Estimate Check
              engine are reachable as secondary compact controls behind a
              disclosure so they no longer read as four competing AI
              products. Every engine handler below is REUSED unchanged.
              Slice 2J correction · The unified Levi estimate-check
              button + findings + advisory toggle are passed via
              `estimateCheckSlot` so the contractor sees ONE Levi-owned
              surface, not two adjacent cards. runDoubleCheck /
              endpoint / deterministic engine / include_ai advisory
              are 100% reused — no new handler, no new endpoint. */}
          <LeviCoworkerCard
            estimate={estimate}
            project={project}
            running={running}
            scopeBanner={scopeBanner}
            doubleCheckStale={isDoubleCheckStale}
            onOrganize={organizeWithAI}
            onRunCheck={runCheck}
            onRunDoubleCheck={runDoubleCheck}
            onJumpToMissingMaterial={scrollToFirstMissingMaterial}
            onJumpToWarnings={scrollToDoubleCheckPanel}
            // Slice 2J · Levi is the sole visible action owner for the
            // estimate check. Suppress Levi's derived-state
            // "Run Double Check" primary button so only ONE primary
            // button is rendered — the estimateCheckSlot below.
            suppressDoubleCheckAction={true}
            estimateCheckSlot={
              <>
                {/* Slice 2J correction · The single Levi-branded
                    estimate-check primary. Same runDoubleCheck handler,
                    same POST /api/estimates/{id}/double-check endpoint,
                    same deterministic engine — only the contractor-
                    facing wording changed. testid preserved for the
                    Slice 2I "Double Check trigger contractor-controlled
                    and unchanged" invariant. */}
                <button
                  onClick={runDoubleCheck}
                  disabled={runningDoubleCheck}
                  data-testid="run-double-check-btn"
                  className="w-full text-left inline-flex items-center justify-between gap-3 rounded-md px-4 py-3 jc-btn-primary disabled:opacity-60"
                >
                  <span className="inline-flex items-center gap-2">
                    <ShieldCheck size={16} />
                    <span className="font-semibold">
                      {runningDoubleCheck
                        ? t("estimates_form.levi_estimate_check_running")
                        : t("estimates_form.levi_estimate_check_cta")}
                    </span>
                  </span>
                  {estimate.double_check && (
                    <DoubleCheckSeverityBadge
                      severity={estimate.double_check.overall_severity}
                      stale={isDoubleCheckStale}
                    />
                  )}
                </button>
                <p
                  className="text-xs text-[var(--jc-text-soft)]"
                  data-testid="levi-estimate-check-help"
                >
                  {t("estimates_form.levi_estimate_check_help")}
                </p>
                {/* iter34 · finding-count summary + jump-to-findings.
                    Rendered here inside the same Levi surface so
                    findings never live in a competing card. */}
                {estimate.double_check && (() => {
                  const red = Number(estimate.double_check.red_count) || 0;
                  const yellow = Number(estimate.double_check.yellow_count) || 0;
                  const total = red + yellow;
                  if (total === 0) return null;
                  const label = t("estimates_form.double_check_warnings", total);
                  return (
                    <div
                      data-testid="double-check-findings-summary"
                      className="flex items-center justify-between gap-3 rounded-md border border-[var(--jc-border)] bg-white px-3 py-2"
                    >
                      <div className="flex items-start gap-2 text-sm">
                        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-yellow-700" />
                        <span data-testid="double-check-findings-count-label">
                          <b>{label}</b>
                          {red > 0 && <span className="text-[var(--jc-text-soft)]"> · {t("estimates_form.double_check_red_suffix", red)}</span>}
                          {yellow > 0 && <span className="text-[var(--jc-text-soft)]"> · {t("estimates_form.double_check_yellow_suffix", yellow)}</span>}
                        </span>
                      </div>
                      <button
                        onClick={scrollToDoubleCheckPanel}
                        data-testid="double-check-jump-to-findings-btn"
                        title={t("estimates_form.double_check_jump_hint")}
                        className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                      >
                        {t("estimates_form.view_warnings")} <ArrowDown size={12} />
                      </button>
                    </div>
                  );
                })()}
                {/* Slice 2J correction · The visible "Include AI advisory"
                    checkbox is removed per Product Owner correction §9.
                    The underlying advisory capability in the backend
                    (POST /estimates/{id}/double-check body field
                    `include_ai`) remains untouched — Gate A confirmed
                    it is safe to keep INTERNAL AND OFF. The frontend
                    always sends `include_ai: false` so only the
                    deterministic engine runs. This preserves the
                    optional advisory implementation for a later
                    Product Owner decision without exposing a
                    contractor-facing on/off choice. */}
              </>
            }
          />

          {/* AI scope panel */}
          {scopeBanner && <ScopeResultBanner banner={scopeBanner} onDismiss={() => setScopeBanner(null)} t={t} />}
          {estimate.ai_scope && <AIScopePanel scope={estimate.ai_scope} t={t} />}

          {/* Photos & documents (feeds into AI Scope Organizer) */}
          <section className="jc-card p-6">
            <div className="flex items-center gap-2 mb-3">
              <Camera size={16} className="text-[var(--jc-text-soft)]" />
              <div className="overline">{t("estimates_form.photos_heading")}</div>
            </div>
            <p className="text-xs text-[var(--jc-text-soft)] mb-3">
              {t("estimates_form.photos_body")}
            </p>
            <PhotoUploader projectId={estimate.project_id} />
          </section>

          {/* Levi Complete Scope Orchestration · advisory manifest.
              Auto-loads on estimate open. Contractor-triggered actions
              only. No silent estimate mutation. */}
          {estimate?.id && estimate?.project_id && (
            <LeviScopeOrchestrator
              estimateId={estimate.id}
              projectId={estimate.project_id}
              onLineItemAdded={(resp) => {
                if (resp && resp.line_item) {
                  setEstimate((prev) => {
                    if (!prev) return prev;
                    const items = [...(prev.line_items || []), resp.line_item];
                    const next = { ...prev, line_items: items };
                    if (resp.totals) Object.assign(next, resp.totals);
                    return next;
                  });
                }
              }}
            />
          )}

          {/* Slice VI-03 · Contractor-Reviewed Levi Estimate Suggestions.
              Reads eligible approved Work Areas and offers per-item
              contractor review + explicit commit. Never mutates the
              estimate on read or per-item action; only on the
              confirmed Commit-to-estimate action. */}
          {estimate?.id && estimate?.project_id && (
            <LeviEstimateSuggestionsSection
              projectId={estimate.project_id}
              estimateId={estimate.id}
              estimateName={estimate?.name || estimate?.id} />
          )}

          {/* Line items */}
          <section className="jc-card overflow-hidden">
            <MaterialIncompleteBanner counts={incompleteCounts()} t={t} />
            <div className="p-5 border-b border-[var(--jc-border)] flex items-center justify-between">
              <div>
                <div className="overline">{t("estimates_form.builder_overline")}</div>
                <h3 className="font-display text-lg">{t("estimates_form.builder_heading")}</h3>
              </div>
              <button onClick={addLine} data-testid="add-line-btn" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-semibold border border-[var(--jc-border-strong)]">
                <Plus size={14} /> {t("estimates_form.add_line")}
              </button>
            </div>
            <div className="p-5 space-y-4">
              {estimate.line_items.length === 0 ? (
                <div className="px-5 py-8 text-center text-[var(--jc-text-soft)] border border-dashed border-[var(--jc-border-strong)] rounded-md">
                  {t("estimates_form.empty_lines")}
                </div>
              ) : estimate.line_items.map((l, idx) => (
                <LineItemCard
                  key={l.id}
                  idx={idx}
                  line={l}
                  t={t}
                  isFirst={idx === 0}
                  isLast={idx === estimate.line_items.length - 1}
                  updateLine={updateLine}
                  removeLine={removeLine}
                  duplicateLine={duplicateLine}
                  moveLineUp={moveLineUp}
                  moveLineDown={moveLineDown}
                  onMaterialBasisChange={onMaterialBasisChange}
                  onOpenPriceBook={() => setPriceBookPickerIdx(idx)}
                  onClearPriceBookLink={() =>
                    patchLine(idx, { price_book_ref: null, material_snapshot: null })
                  }
                  onOpenWorkAreaStaging={() => setWaStagingIdx(idx)}
                />
              ))}
            </div>
          </section>

          {waStagingIdx !== null && estimate?.line_items?.[waStagingIdx] && estimate?.project_id && estimate?.id && (
            <WorkAreaStagingPicker
              projectId={estimate.project_id}
              estimateId={estimate.id}
              lineIndex={waStagingIdx}
              currentQuantity={estimate.line_items[waStagingIdx].quantity}
              currentUnit={estimate.line_items[waStagingIdx].unit}
              currentContractorEdited={estimate.line_items[waStagingIdx].quantity_contractor_edited === true}
              onStaged={(resp) => {
                if (resp && resp.line_item) {
                  setEstimate((prev) => {
                    if (!prev) return prev;
                    const items = [...prev.line_items];
                    items[waStagingIdx] = resp.line_item;
                    // Any prior Double Check payload is stale — the
                    // backend already marks it stale server-side; we
                    // mirror it here so the coworker card reacts
                    // immediately without waiting for a refetch.
                    const next = { ...prev, line_items: items };
                    if (resp.totals) Object.assign(next, resp.totals);
                    return next;
                  });
                  if (resp.result === "already_staged") {
                    toast.info(t("work_areas.staging.already_staged"));
                  } else {
                    toast.success(t("work_areas.staging.staged_ok"));
                  }
                }
              }}
              onClose={() => setWaStagingIdx(null)}
            />
          )}

          {estimate.ai_check && (
            <AICheckPanel
              check={estimate.ai_check}
              t={t}
              prependFindings={[
                ...buildProposalIssues().map((msg) => ({
                  category: "Pricing",
                  severity: "red",
                  title: t("estimates_form.aicheck_pricing_safety_title"),
                  detail: msg,
                  suggestion: t("estimates_form.aicheck_pricing_safety_suggestion"),
                })),
                ...(!((profile?.standard_material_notes || "").trim())
                  ? [{
                      category: "missing_profile_rules",
                      severity: "red",
                      title: t("estimates_form.aicheck_missing_rules_title"),
                      detail: t("estimates_form.aicheck_missing_rules_detail", profile?.trade),
                      suggestion: t("estimates_form.aicheck_missing_rules_suggestion"),
                    }]
                  : []),
              ]}
            />
          )}
          {estimate.double_check && (
            <div ref={doubleCheckPanelRef} id="double-check-panel" data-testid="double-check-panel-anchor" className="scroll-mt-24">
              <DoubleCheckPanel
                check={estimate.double_check}
                t={t}
                stale={isDoubleCheckStale}
                onRerun={runDoubleCheck}
                running={runningDoubleCheck}
              />
            </div>
          )}
        </div>

        {/* Totals sidebar — reads from livePreview (client-computed) so it
            updates the moment the contractor types a qty or rate. On Save
            Estimate the server recomputes and the response overwrites
            estimate.subtotal/total/etc.; the preview then matches. */}
        <aside className="lg:col-span-4">
          <div className="jc-card p-6 sticky top-24">
            <div className="overline">{t("estimates_form.totals_overline")}</div>
            <h3 className="font-display text-2xl mt-2" data-testid="totals-live-total">{money(livePreview.total)}</h3>
            {(livePreview.total || 0) <= 0 && (
              <div
                data-testid="totals-zero-warning"
                className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[var(--jc-red)]/10 text-[var(--jc-red)] border border-[var(--jc-red)]/30"
              >
                <AlertTriangle size={11} /> {t("estimates_form.totals_no_priced")}
              </div>
            )}
            {(livePreview.total || 0) > 0 && incompleteCounts().needMaterial > 0 && (
              <div
                data-testid="totals-material-warning"
                className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[var(--jc-red)]/10 text-[var(--jc-red)] border border-[var(--jc-red)]/30"
                title={t("estimates_form.totals_material_missing_title")}
              >
                <AlertTriangle size={11} /> {t("estimates_form.totals_material_missing")}
              </div>
            )}
            <dl className="mt-5 space-y-2 text-sm">
              <Row label={t("estimates_form.totals_subtotal")} value={<span data-testid="totals-subtotal">{money(livePreview.subtotal)}</span>} />
              <Row label={t("estimates_form.totals_tax_rate")}>
                <div className="flex items-center gap-1">
                  <input type="number" value={estimate.tax_rate} onChange={(e) => { setEstimate({ ...estimate, tax_rate: parseFloat(e.target.value || 0) }); markDirty(); }} className="w-16 text-right px-2 py-1 rounded border border-[var(--jc-border)] font-mono-jc" />
                  <span className="text-[var(--jc-text-soft)]">%</span>
                </div>
              </Row>
              <Row label={t("estimates_form.totals_tax")} value={<span data-testid="totals-tax">{money(livePreview.tax)}</span>} />
              <Row label={t("estimates_form.totals_total")} value={<span className="font-bold" data-testid="totals-total">{money(livePreview.total)}</span>} />
              <Row label={t("estimates_form.totals_deposit_pct")}>
                <div className="flex items-center gap-1">
                  <input type="number" value={estimate.deposit_pct} onChange={(e) => { setEstimate({ ...estimate, deposit_pct: parseFloat(e.target.value || 0) }); markDirty(); }} className="w-16 text-right px-2 py-1 rounded border border-[var(--jc-border)] font-mono-jc" />
                  <span className="text-[var(--jc-text-soft)]">%</span>
                </div>
              </Row>
              <Row label={t("estimates_form.totals_deposit")} value={<span data-testid="totals-deposit">{money(livePreview.deposit)}</span>} />
              <Row label={t("estimates_form.totals_balance")} value={<span data-testid="totals-balance">{money(livePreview.balance_due)}</span>} />
            </dl>
            <button onClick={save} disabled={running.save} data-testid="totals-save-btn" className="w-full mt-5 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60">
              <Save size={14} /> {running.save ? t("estimates_form.saving") : t("estimates_form.save_estimate")}
            </button>
          </div>
        </aside>
      </div>

      {/* Proposal $0 / unpriced-items guard.

          iter55b · Two modes:
            • soft (hardBlock !== true) — legacy behavior: warns about
              things like missing names or a $0 tax rate, contractor may
              still "Send anyway".
            • hard (hardBlock === true) — the backend will 409 on send,
              so no "Send anyway" is offered. Contractor gets
              "Back to edit pricing" and "Complete Pricing Setup". */}
      <AlertDialog open={!!proposalGuard} onOpenChange={(open) => { if (!open) setProposalGuard(null); }}>
        <AlertDialogContent
          data-testid="proposal-guard-dialog"
          data-hardblock={proposalGuard?.hardBlock ? "true" : "false"}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2" data-testid="proposal-guard-title">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[var(--jc-red)]/10 text-[var(--jc-red)]">
                <AlertTriangle size={18} />
              </span>
              {proposalGuard?.headline || t("estimates_form.guard_soft_headline")}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {proposalGuard?.hardBlock ? (
                  <p className="mb-3" data-testid="proposal-guard-hardblock-copy">
                    {t("estimates_form.guard_hard_body")}
                  </p>
                ) : (
                  <p className="mb-3">{t("estimates_form.guard_soft_body")}</p>
                )}
                <ul className="list-disc pl-5 space-y-1 text-[var(--jc-text)]">
                  {(proposalGuard?.issues || []).map((msg, i) => (
                    <li key={i} data-testid={`proposal-guard-issue-${i}`}>{msg}</li>
                  ))}
                </ul>
                {!proposalGuard?.hardBlock && (
                  <p className="mt-3 text-xs text-[var(--jc-text-soft)]">
                    {t("estimates_form.guard_soft_footnote")}
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={proposalGuard?.hardBlock ? "gap-2" : ""}>
            {proposalGuard?.hardBlock && (
              <Link
                to="/app/setup#trade-pricing"
                data-testid="proposal-guard-complete-setup-link"
                onClick={() => setProposalGuard(null)}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
              >
                <Sparkles size={12} /> {t("estimates_form.complete_pricing_setup")}
              </Link>
            )}
            <AlertDialogCancel data-testid="proposal-guard-cancel-btn" disabled={sendingProposal}>
              {t("estimates_form.guard_back_edit")}
            </AlertDialogCancel>
            {!proposalGuard?.hardBlock && (
              <AlertDialogAction
                data-testid="proposal-guard-confirm-btn"
                onClick={(e) => { e.preventDefault(); sendProposal(); }}
                disabled={sendingProposal}
                className="bg-[var(--jc-red)] text-white hover:bg-[var(--jc-red)]/90 focus:ring-[var(--jc-red)]"
              >
                {sendingProposal ? t("estimates_form.guard_sending") : t("estimates_form.guard_send_anyway")}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Slice A3.2 — Double Check acknowledgement dialog. Opens when the
          proposal endpoint returned HTTP 409 with double_check_required.
          Contractor must tick the acknowledgement checkbox before the
          confirm button becomes active. This is a soft gate — not a
          permanent block. */}
      <AlertDialog
        open={!!ackDialog}
        onOpenChange={(open) => {
          if (!open && !acknowledging) {
            setAckDialog(null);
            setAckConfirmed(false);
          }
        }}
      >
        <AlertDialogContent
          data-testid="double-check-ack-dialog"
          className="max-w-2xl"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[var(--jc-red)]/10 text-[var(--jc-red)]">
                <ShieldCheck size={18} />
              </span>
              {t("estimates_form.ack_title")}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p className="mb-3 text-[var(--jc-text)]">
                  {t("estimates_form.ack_intro_prefix")}{" "}
                  <b>
                    {t("estimates_form.ack_intro_finding", ackDialog?.doubleCheck?.red_count || 0)}
                  </b>{" "}
                  {t("estimates_form.ack_intro_suffix")}
                </p>
                <div
                  className="max-h-[45vh] overflow-y-auto space-y-2 pr-1"
                  data-testid="double-check-ack-findings"
                >
                  {(ackDialog?.doubleCheck?.findings || [])
                    .filter((f) => f.severity === "red" && (f.source || "local") === "local")
                    .map((f, i) => (
                      <div
                        key={i}
                        data-testid={`ack-finding-${f.category}-${i}`}
                        className="rounded-md border border-red-200 bg-red-50 text-red-900 p-3"
                      >
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-70">
                          {f.category}
                        </div>
                        <div className="font-semibold text-sm mt-1">{f.title}</div>
                        <div className="text-xs mt-1 opacity-90">{f.detail}</div>
                        {f.suggestion && (
                          <div className="text-xs mt-2 italic opacity-80">
                            {t("estimates_form.ack_suggestion_prefix")} {f.suggestion}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
                <label className="mt-4 flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={ackConfirmed}
                    onChange={(e) => setAckConfirmed(e.target.checked)}
                    disabled={acknowledging}
                    data-testid="double-check-ack-checkbox"
                    className="mt-1 h-4 w-4"
                  />
                  <span className="text-sm text-[var(--jc-text)]">
                    {t("estimates_form.ack_checkbox")}
                  </span>
                </label>
                <p className="mt-3 text-xs text-[var(--jc-text-soft)]">
                  {t("estimates_form.ack_hint")}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="double-check-ack-cancel-btn"
              disabled={acknowledging}
            >
              {t("estimates_form.ack_back")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="double-check-ack-confirm-btn"
              onClick={(e) => {
                e.preventDefault();
                acknowledgeAndProceed();
              }}
              disabled={!ackConfirmed || acknowledging}
              className="bg-[var(--jc-red)] text-white hover:bg-[var(--jc-red)]/90 focus:ring-[var(--jc-red)] disabled:opacity-50"
            >
              {acknowledging ? t("estimates_form.ack_confirming") : t("estimates_form.ack_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Material basis-change conversion prompt */}
      <AlertDialog open={!!basisPrompt} onOpenChange={(open) => { if (!open) setBasisPrompt(null); }}>
        <AlertDialogContent data-testid="material-basis-prompt-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("estimates_form.basis_title")}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>
                  {t("estimates_form.basis_prompt_switch", basisPrompt?.qtyUnit, basisPrompt?.newBasis)}
                </p>
                <p className="mt-2">
                  {t("estimates_form.basis_prompt_ratio", basisPrompt?.newBasis, basisPrompt?.qtyUnit)}
                </p>
                <p className="text-xs text-[var(--jc-text-soft)] mt-1">
                  {t("estimates_form.basis_prompt_examples")}
                </p>
                <input
                  data-testid="material-basis-prompt-factor"
                  id="basis-factor-input"
                  type="number"
                  step="any"
                  placeholder={`# ${basisPrompt?.newBasis} per ${basisPrompt?.qtyUnit}`}
                  className="w-full mt-3 px-3 py-2 rounded border border-[var(--jc-border)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]"
                  autoFocus
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="material-basis-prompt-cancel">{t("estimates_form.basis_cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="material-basis-prompt-apply"
              onClick={(e) => {
                e.preventDefault();
                const input = document.getElementById("basis-factor-input");
                applyBasisConversion(input?.value);
              }}
            >
              {t("estimates_form.basis_apply")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {priceBookPickerIdx !== null && (
        <PriceBookPickerModal
          t={t}
          onSelect={(item) => {
            // Build the customer-safe field patch + the internal snapshot.
            const patch = {
              unit: item.unit || "each",
              material_basis_unit: item.unit || "each",
              material_cost: Number(item.contractor_cost) || 0,
              price_book_ref: item.id,
              material_snapshot: {
                supplier: item.supplier || "",
                brand: item.brand || "",
                product: item.product || "",
                sku: item.sku || "",
                unit: item.unit || "",
                contractor_cost: Number(item.contractor_cost) || 0,
                waste_pct: Number(item.waste_pct) || 0,
                coverage_per_unit: item.coverage_per_unit ?? null,
                markup_pct: item.markup_pct ?? null,
                snapshot_at: new Date().toISOString(),
              },
            };
            // Only prefill name if the contractor hasn't already typed one.
            const currentName = ((estimate.line_items[priceBookPickerIdx] || {}).name || "").trim();
            if (!currentName) patch.name = item.product;
            // Respect the item's markup override when it explicitly set one.
            if (item.markup_pct !== null && item.markup_pct !== undefined && item.markup_pct !== "") {
              patch.markup_pct = Number(item.markup_pct);
            }
            patchLine(priceBookPickerIdx, patch);
            setPriceBookPickerIdx(null);
            toast.success(t("estimates_form.toast_pb_linked", item.product));
          }}
          onClose={() => setPriceBookPickerIdx(null)}
        />
      )}
      {/* Slice 2J amendment · Centered Levi acknowledgement — appears
          after Edit Estimate save, dims the background, holds for
          ~2.5s, and auto-dismisses. No contractor click required. */}
      {leviAck && (
        <div
          data-testid="levi-edit-ack-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity duration-300"
          aria-live="polite"
          role="status"
        >
          <div className="mx-4 max-w-md rounded-xl bg-white px-8 py-7 text-center shadow-2xl">
            <img
              src="/levi/levi-sitting.webp"
              alt="Levi, your coworker at JobClose AI"
              data-testid="levi-edit-ack-image"
              className="mx-auto mb-4 h-24 w-24 object-contain"
            />
            <div className="text-lg font-semibold text-[var(--jc-text)]" data-testid="levi-edit-ack-line-1">
              {t("estimates_form.edit_ack_line_1")}
            </div>
            <div className="mt-2 text-sm text-[var(--jc-text-soft)]" data-testid="levi-edit-ack-line-2">
              {t("estimates_form.edit_ack_line_2")}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

const formatMoney = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};

// Slice 2B1 — Approved installed-selling line card. Renders a compact,
// read-only-priced row for lines inserted from Trade Setup ("Use approved
// pricing"). The contractor edits only Name, Description and Quantity.
// The Unit and Approved unit price are locked because they were approved
// during Trade Setup and re-validated on save by `validate_and_normalize_lines`.
// Any material / labor / markup / pricing-method controls are hidden — they
// are irrelevant for this pricing type.
const InstalledSellingLineCard = ({ idx, line, t, isFirst, isLast, updateLine, removeLine, duplicateLine, moveLineUp, moveLineDown, onOpenWorkAreaStaging }) => {
  const qty = Number(line.quantity) || 0;
  const spu = Number(line.selling_price_per_unit) || 0;
  const unit = (line.selling_price_unit || line.unit || "").trim() || "each";
  const lineTotal = qty * spu;
  const missingQty = qty <= 0;
  // "Enter quantity" — never "Needs pricing" — because the approved rate
  // came from Trade Setup and is the trusted price. The only thing the
  // contractor must supply is the quantity for THIS job.
  const pillLabel = missingQty ? t("estimates_form.line_needs_quantity") : null;
  const flagged = missingQty;
  // Slice 2B1 — Quantity provenance UI. Shows "From job measurements:
  // 20 roofing squares." when the quantity was auto-populated. Once the
  // contractor edits the value, the copy switches to a distinct
  // "Adjusted from job measurements ..." affordance so the reviewer can
  // see WHERE the initial value came from AND that it was manually
  // overridden.
  const hasProvenance = Boolean(line.quantity_source && line.quantity_source_text);
  const isEdited = line.quantity_contractor_edited === true;

  return (
    <div
      id={line?.id ? `estimate-line-${line.id}` : undefined}
      data-testid={`line-card-${idx}`}
      className={`rounded-lg border ${flagged ? "border-yellow-300 bg-yellow-50/40" : "border-emerald-200 bg-emerald-50/20"} p-4`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              data-testid={`line-approved-badge-${idx}`}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300"
              title="Priced from your approved Trade Setup pricing. Edit the rate in Trade Setup, not here."
            >
              {t("estimates_form.line_approved")}
            </span>
            {line.auto_staged && (
              <>
                <span
                  data-testid={`line-matched-from-settings-${idx}`}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200"
                  title="Matched from your Pricing Settings — the approved system and rate came from Trade Setup."
                >
                  {t("estimates_form.line_matched_settings")}
                </span>
                <span
                  data-testid={`line-review-required-${idx}`}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200"
                  title="This draft was staged automatically. Review and accept before generating the proposal."
                >
                  {t("estimates_form.line_review_required")}
                </span>
              </>
            )}
            <input
              data-testid={`line-name-${idx}`}
              value={line.name || ""}
              onChange={(e) => updateLine(idx, "name", e.target.value)}
              placeholder={t("estimates_form.line_item_name_placeholder")}
              className="flex-1 min-w-[200px] px-2 py-1.5 rounded border border-[var(--jc-border)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)] font-semibold"
            />
            {pillLabel && (
              <span
                data-testid={`line-needs-quantity-${idx}`}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--jc-yellow)]/20 text-yellow-900 border border-yellow-300"
                title={t("estimates_form.line_needs_qty_title")}
              >
                <AlertTriangle size={10} /> {pillLabel}
              </span>
            )}
          </div>
          <input
            data-testid={`line-description-${idx}`}
            value={line.description || ""}
            onChange={(e) => updateLine(idx, "description", e.target.value)}
            placeholder={t("estimates_form.line_description_installed_placeholder")}
            className="w-full mt-2 px-2 py-1 rounded border border-[var(--jc-border)] text-xs text-[var(--jc-text-soft)]"
          />
        </div>
        <div className="flex items-center gap-1 mt-1 shrink-0">
          {!isFirst && (
            <button
              onClick={() => moveLineUp(idx)}
              data-testid={`line-move-up-${idx}`}
              className="text-[var(--jc-text-soft)] hover:text-[var(--jc-navy)] p-0.5"
              aria-label={t("estimates_form.line_move_up")}
              title={t("estimates_form.line_move_up")}
            >
              <ArrowUp size={14} />
            </button>
          )}
          {!isLast && (
            <button
              onClick={() => moveLineDown(idx)}
              data-testid={`line-move-down-${idx}`}
              className="text-[var(--jc-text-soft)] hover:text-[var(--jc-navy)] p-0.5"
              aria-label={t("estimates_form.line_move_down")}
              title={t("estimates_form.line_move_down")}
            >
              <ArrowDown size={14} />
            </button>
          )}
          <button
            onClick={() => duplicateLine(idx)}
            data-testid={`line-duplicate-${idx}`}
            className="text-[var(--jc-text-soft)] hover:text-[var(--jc-navy)] p-0.5"
            aria-label={t("estimates_form.line_duplicate")}
            title={t("estimates_form.line_duplicate")}
          >
            <Copy size={14} />
          </button>
          <button
            onClick={() => removeLine(idx)}
            data-testid={`line-remove-${idx}`}
            className="text-[var(--jc-text-soft)] hover:text-[var(--jc-red)] p-0.5"
            aria-label={t("estimates_form.line_remove")}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Body — Quantity, Unit (locked), Approved unit price (locked), Line total. */}
      <div className="mt-4 grid md:grid-cols-4 gap-3 text-sm items-end">
        <div className="rounded-md border border-[var(--jc-border)] bg-white p-3">
          <div className="overline mb-1.5">{t("estimates_form.line_quantity")}</div>
          <input
            data-testid={`line-qty-${idx}`}
            type="number"
            step="any"
            value={line.quantity ?? ""}
            onChange={(e) => updateLine(idx, "quantity", e.target.value)}
            className="w-full text-right px-2 py-1 rounded border border-[var(--jc-border)] font-mono-jc"
            placeholder={hasProvenance ? t("estimates_form.line_quantity_prefilled_placeholder") : t("estimates_form.line_quantity_enter_placeholder")}
          />
          {hasProvenance ? (
            <div
              data-testid={`line-quantity-source-${idx}`}
              className="text-[11px] text-[var(--jc-text-soft)] mt-1.5"
              title={
                line.quantity_converted
                  ? `Auto-converted (${line.quantity_conversion_rule || "unit conversion"}). Edit for this job only — the saved pricing rule is unchanged.`
                  : "Edit for this job only — the saved pricing rule is unchanged."
              }
            >
              {isEdited ? t("estimates_form.line_adjusted_from_prefix") : t("estimates_form.line_from_prefix")}{line.quantity_source === "job_measurements" ? t("estimates_form.line_from_job_meas") : line.quantity_source === "work_area" ? t("estimates_form.line_from_work_area") : line.quantity_source?.replace(/_/g, " ") || "notes"}: <b className="text-[var(--jc-text)]">{line.quantity_source_text}</b>
              {line.quantity_converted && line.quantity_source_display_value != null && (
                <span>{t("estimates_form.line_converted_to", line.quantity_source_display_value, unit)}</span>
              )}
              {isEdited && <span data-testid={`line-quantity-edited-${idx}`}>{t("estimates_form.line_edited_suffix")}</span>}
            </div>
          ) : (
            <div className="text-[11px] text-[var(--jc-text-soft)] mt-1.5">
              {missingQty ? t("estimates_form.line_qty_select") : t("estimates_form.line_qty_default")}
            </div>
          )}
          {onOpenWorkAreaStaging && (
            <button
              type="button"
              onClick={onOpenWorkAreaStaging}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--jc-blue)] hover:text-[var(--jc-navy)] underline underline-offset-2 decoration-dotted"
              data-testid={`line-use-approved-measurement-${idx}`}
              title={t("estimates_form.line_use_approved_measurement")}
            >
              {t("estimates_form.line_use_approved_measurement")}
            </button>
          )}
        </div>
        <div className="rounded-md border border-[var(--jc-border)] bg-white p-3">
          <div className="overline mb-1.5">{t("estimates_form.line_unit")}</div>
          <div
            data-testid={`line-installed-unit-${idx}`}
            className="px-2 py-1 rounded border border-transparent bg-[var(--jc-surface)]/60 font-mono-jc text-[var(--jc-text)]"
            title={t("estimates_form.line_unit_locked_title")}
          >
            {unit}
          </div>
          <div className="text-[11px] text-[var(--jc-text-soft)] mt-1.5">
            {t("estimates_form.line_unit_locked_body")}
          </div>
        </div>
        <div className="rounded-md border border-[var(--jc-border)] bg-white p-3">
          <div className="overline mb-1.5">{t("estimates_form.line_approved_price")}</div>
          <div
            data-testid={`line-installed-rate-${idx}`}
            className="px-2 py-1 rounded border border-transparent bg-[var(--jc-surface)]/60 font-mono-jc font-semibold text-right text-[var(--jc-text)]"
            title={t("estimates_form.line_approved_price_title")}
          >
            {formatMoney(spu)}
          </div>
          <div className="text-[11px] text-[var(--jc-text-soft)] mt-1.5">
            {t("estimates_form.line_per_unit", unit)}
          </div>
        </div>
        <div className="rounded-md border border-emerald-200 bg-white p-3 text-right">
          <div className="overline mb-1.5">{t("estimates_form.line_total")}</div>
          <div
            data-testid={`line-total-${idx}`}
            className="font-mono-jc font-semibold text-lg"
          >
            {missingQty ? "—" : formatMoney(lineTotal)}
          </div>
          <div className="text-[11px] text-[var(--jc-text-soft)] mt-1.5">
            {missingQty ? t("estimates_form.line_needs_quantity") : `${qty} × ${formatMoney(spu)}`}
          </div>
        </div>
      </div>
      <div className="mt-2 text-[11px] text-emerald-800 leading-relaxed"
           data-testid={`line-complete-installed-copy-${idx}`}>
        <b>{t("estimates_form.line_installed_prefix")}</b> {t("estimates_form.line_installed_body")}
      </div>
      <div className="mt-1 text-[11px] text-[var(--jc-text-soft)] leading-relaxed">
        {t("estimates_form.line_installed_note_prefix")} <b>{t("estimates_form.line_installed_note_this_job")}</b>{t("estimates_form.line_installed_note_suffix")}<a href="/app/setup?step=trade_setup" className="underline">{t("estimates_form.line_installed_note_link")}</a>.
      </div>
    </div>
  );
};

const LineItemCard = ({ idx, line, t, isFirst, isLast, updateLine, removeLine, duplicateLine, moveLineUp, moveLineDown, onMaterialBasisChange, onOpenPriceBook, onClearPriceBookLink, onOpenWorkAreaStaging }) => {
  // Slice 2B1 — Approved installed-selling line from Trade Setup renders as
  // a dedicated read-only-priced card: Name, Description, Quantity + Unit
  // (unit is the approved selling_price_unit and read-only), Approved unit
  // price (read-only display), Line total = qty × approved rate. All the
  // material / labor / markup / pricing-method blocks below are IRRELEVANT
  // for this pricing type and MUST NOT render (else the line looks like a
  // "Standard (labor only)" row with a lost rate).
  if (isInstalledSellingLine(line)) {
    return (
      <InstalledSellingLineCard
        idx={idx}
        line={line}
        t={t}
        isFirst={isFirst}
        isLast={isLast}
        updateLine={updateLine}
        removeLine={removeLine}
        duplicateLine={duplicateLine}
        moveLineUp={moveLineUp}
        moveLineDown={moveLineDown}
        onOpenWorkAreaStaging={onOpenWorkAreaStaging}
      />
    );
  }

  const unpriced = isLineUnpriced(line);
  const needsMat = isLineNeedsMaterial(line);
  const pillLabel = unpriced ? t("estimates_form.line_needs_pricing") : needsMat ? t("estimates_form.line_needs_material") : null;
  const flagged = unpriced || needsMat;

  // Slice F pricing method — when the contractor picked a sell-price mode
  // (unit_price or flat), the legacy Quantity / Material / Labor / Markup /
  // line-total blocks are IRRELEVANT to the actual line total (backend
  // ``_normalize_line_items_for_pricing_method`` overrides using unit_qty ×
  // unit_price or flat_price). Rendering them next to the correct total was
  // showing a confusing "$0.00" secondary line total for Roofing Simple
  // Builder labor packages. Hide them for those modes — the pricing-method
  // block itself already shows Qty / Unit / Unit price / Line total.
  const pmMode = (line.pricing_method || "").trim();
  const isFixedPriceMode = pmMode === "unit_price" || pmMode === "flat";
  // Rio dry-run finding #6 (iter31) — trade-agnostic display fix.
  // Rows the AI Organizer (or the contractor) explicitly marked as pure labor /
  // fee (material_required === false) should NOT render a confusing material
  // block in Standard mode. Backend math already treats material as 0 on these
  // rows; this is display-only. Works across every trade (flooring, painting,
  // drywall, roofing tear-off/disposal, permits, handyman punch, cleanup).
  const isLaborOnlyRow = line.material_required === false;

  const qty = Number(line.quantity) || 0;
  const unit = line.unit || "each";
  // Material basis (may differ from qty unit). Defaults to qty unit.
  const matBasis = line.material_basis_unit || unit;
  const matQty = (line.material_qty === null || line.material_qty === undefined || line.material_qty === "")
    ? qty
    : (Number(line.material_qty) || 0);
  const matCost = Number(line.material_cost) || 0;
  const matTotal = matQty * matCost;

  const laborBasis = (line.labor_basis || "").trim() || "hour";
  const laborQty = Number(line.labor_qty ?? 0) || 0;
  const laborRate = Number(line.labor_unit_cost ?? 0) || 0;
  const laborFromBasis = laborQty * laborRate;
  const laborTotal = (line.labor_basis && laborQty > 0 && laborRate > 0)
    ? laborFromBasis
    : (Number(line.labor_cost) || 0);
  const markup = Number(line.markup_pct) || 0;
  const lineTotal = (matTotal + laborTotal) * (1 + markup / 100);

  // Labor basis dropdown options.
  const laborBasisOptions = Array.from(new Set(["hour", unit, "job"]));
  // Material basis dropdown options — always include qty unit + common alternatives.
  const materialBasisOptions = Array.from(new Set([unit, ...UNIT_OPTIONS]));

  // Detect legacy rows so the labor helper text doesn't show "0 × 0 = $X" arithmetic.
  const isLegacyLaborOnly =
    !line.labor_basis &&
    (!line.labor_qty || Number(line.labor_qty) === 0) &&
    (!line.labor_unit_cost || Number(line.labor_unit_cost) === 0) &&
    (Number(line.labor_cost) || 0) > 0;

  const matMathTxt = matTotal > 0
    ? t("estimates_form.line_material_math_priced", matQty, matBasis, formatMoney(matCost), formatMoney(matTotal))
    : t("estimates_form.line_material_math_needed", qty, unit);
  const labMathTxt = isLegacyLaborOnly
    ? t("estimates_form.line_labor_math_legacy", formatMoney(laborTotal))
    : laborBasis === "hour"
      ? t("estimates_form.line_labor_math_hour", laborQty, formatMoney(laborRate), formatMoney(laborTotal))
      : laborBasis === "job"
        ? t("estimates_form.line_labor_math_job", formatMoney(laborTotal))
        : t("estimates_form.line_labor_math_basis", laborQty, laborBasis, formatMoney(laborRate), formatMoney(laborTotal));

  return (
    <div
      id={line?.id ? `estimate-line-${line.id}` : undefined}
      data-testid={`line-card-${idx}`}
      className={`rounded-lg border ${flagged ? "border-yellow-300 bg-yellow-50/40" : "border-[var(--jc-border)] bg-white"} p-4`}
    >
      {/* Header: name + pill + remove */}
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              data-testid={`line-name-${idx}`}
              value={line.name}
              onChange={(e) => updateLine(idx, "name", e.target.value)}
              placeholder={t("estimates_form.line_item_name_std_placeholder")}
              className="flex-1 min-w-[200px] px-2 py-1.5 rounded border border-[var(--jc-border)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)] font-semibold"
            />
            {pillLabel && (
              <span
                data-testid={unpriced ? `line-needs-pricing-${idx}` : `line-needs-material-${idx}`}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--jc-yellow)]/20 text-yellow-900 border border-yellow-300"
                title={unpriced ? t("estimates_form.line_needs_pricing_title") : t("estimates_form.line_needs_material_title")}
              >
                <AlertTriangle size={10} /> {pillLabel}
              </span>
            )}
          </div>
          <input
            data-testid={`line-description-${idx}`}
            value={line.description}
            onChange={(e) => updateLine(idx, "description", e.target.value)}
            placeholder={t("estimates_form.line_description_std_placeholder")}
            className="w-full mt-2 px-2 py-1 rounded border border-[var(--jc-border)] text-xs text-[var(--jc-text-soft)]"
          />
          {/* Slice F — Pricing method selector (contractor-facing). Legacy
              lines have pricing_method=undefined → "standard" behavior. */}
          <PricingMethodSelector idx={idx} line={line} updateLine={updateLine} isLaborOnlyRow={isLaborOnlyRow} t={t} />
          {/* Slice A2a — Materials Price Book link on this line (contractor-internal). */}
          <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`line-pricebook-controls-${idx}`}>
            {line.price_book_ref ? (
              <>
                <span
                  data-testid={`line-pricebook-linked-${idx}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200"
                  title={t("estimates_form.pb_snapshot_title")}
                >
                  <Link2 size={11} /> {t("estimates_form.pb_linked_prefix")} {(line.material_snapshot && line.material_snapshot.product) || t("estimates_form.pb_linked_fallback")}
                </span>
                <button
                  onClick={onOpenPriceBook}
                  data-testid={`line-pricebook-change-${idx}`}
                  className="text-[11px] font-semibold text-[var(--jc-blue)] hover:underline"
                >
                  {t("estimates_form.pb_change")}
                </button>
                <button
                  onClick={onClearPriceBookLink}
                  data-testid={`line-pricebook-clear-${idx}`}
                  className="text-[11px] font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-red)]"
                >
                  {t("estimates_form.pb_clear")}
                </button>
              </>
            ) : (
              <button
                onClick={onOpenPriceBook}
                data-testid={`line-pricebook-open-${idx}`}
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded border border-dashed border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] text-[var(--jc-text-soft)]"
                title={t("estimates_form.pb_snapshot_title")}
              >
                <PackageOpen size={11} /> {t("estimates_form.pb_insert")}
              </button>
            )}
            <span className="text-[10px] text-[var(--jc-text-soft)]">
              {t("estimates_form.pb_disclosure")}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 mt-1 shrink-0">
          {!isFirst && (
            <button
              onClick={() => moveLineUp(idx)}
              data-testid={`line-move-up-${idx}`}
              className="text-[var(--jc-text-soft)] hover:text-[var(--jc-navy)] p-0.5"
              aria-label={t("estimates_form.line_move_up")}
              title={t("estimates_form.line_move_up")}
            >
              <ArrowUp size={14} />
            </button>
          )}
          {!isLast && (
            <button
              onClick={() => moveLineDown(idx)}
              data-testid={`line-move-down-${idx}`}
              className="text-[var(--jc-text-soft)] hover:text-[var(--jc-navy)] p-0.5"
              aria-label={t("estimates_form.line_move_down")}
              title={t("estimates_form.line_move_down")}
            >
              <ArrowDown size={14} />
            </button>
          )}
          <button
            onClick={() => duplicateLine(idx)}
            data-testid={`line-duplicate-${idx}`}
            className="text-[var(--jc-text-soft)] hover:text-[var(--jc-navy)] p-0.5"
            aria-label={t("estimates_form.line_duplicate")}
            title={t("estimates_form.line_duplicate")}
          >
            <Copy size={14} />
          </button>
          <button
            onClick={() => removeLine(idx)}
            data-testid={`line-remove-${idx}`}
            className="text-[var(--jc-text-soft)] hover:text-[var(--jc-red)] p-0.5"
            aria-label={t("estimates_form.line_remove")}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Legacy Quantity / Material / Labor / Markup / Line-total blocks —
          hidden entirely when the line uses a Slice F fixed-price mode
          (unit_price or flat). The pricing-method selector above already
          renders the correct Qty / Unit / Unit price / Line total for
          those modes. */}
      {!isFixedPriceMode && (
        <>
          {/* Body: 3 math blocks — Quantity / Material / Labor. When the row
              is labor-only (material_required === false, iter31), the Material
              cell is hidden and the grid collapses to 2 cols so the labor
              block gets more breathing room. Display-only — backend math
              already reads material as 0 on these rows. */}
          <div className={`mt-4 grid ${isLaborOnlyRow ? "md:grid-cols-2" : "md:grid-cols-3"} gap-3 text-sm`}>
        {/* Quantity */}
        <div className="rounded-md border border-[var(--jc-border)] bg-white p-3">
          <div className="overline mb-1.5">{t("estimates_form.line_quantity")}</div>
          <div className="flex items-center gap-1.5">
            <input
              data-testid={`line-qty-${idx}`}
              type="number"
              step="any"
              value={line.quantity}
              onChange={(e) => updateLine(idx, "quantity", e.target.value)}
              className="w-20 text-right px-2 py-1 rounded border border-[var(--jc-border)] font-mono-jc"
            />
            <select
              data-testid={`line-unit-${idx}`}
              value={line.unit || "each"}
              onChange={(e) => updateLine(idx, "unit", e.target.value)}
              className="flex-1 min-w-0 px-2 py-1 rounded border border-[var(--jc-border)] bg-white"
            >
              {UNIT_OPTIONS.includes(unit) ? null : <option value={unit}>{unit}</option>}
              {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div className="text-[11px] text-[var(--jc-text-soft)] mt-1.5">
            {t("estimates_form.line_qty_helper")}
          </div>
          {onOpenWorkAreaStaging && (
            <button
              type="button"
              onClick={onOpenWorkAreaStaging}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--jc-blue)] hover:text-[var(--jc-navy)] underline underline-offset-2 decoration-dotted"
              data-testid={`line-use-approved-measurement-${idx}`}
              title={t("estimates_form.line_use_approved_measurement")}
            >
              {t("estimates_form.line_use_approved_measurement")}
            </button>
          )}
          {line.quantity_source === "work_area" && line.quantity_source_text && (
            <div
              className="text-[11px] text-[var(--jc-text-soft)] mt-1.5"
              data-testid={`line-quantity-source-${idx}`}
            >
              {line.quantity_contractor_edited === true
                ? t("estimates_form.line_adjusted_from_prefix")
                : t("estimates_form.line_from_prefix")}
              {t("estimates_form.line_from_work_area")}: <b className="text-[var(--jc-text)]">{line.quantity_source_text}</b>
            </div>
          )}
        </div>

        {/* Material — hidden entirely on labor-only rows (iter31 display fix). */}
        {!isLaborOnlyRow && (
        <div className="rounded-md border border-[var(--jc-border)] bg-white p-3">
          <div className="overline mb-1.5">{t("estimates_form.line_material")}</div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <select
              data-testid={`line-material-basis-${idx}`}
              value={matBasis}
              onChange={(e) => onMaterialBasisChange(idx, e.target.value)}
              className="px-2 py-1 rounded border border-[var(--jc-border)] bg-white text-xs"
              title={t("estimates_form.line_material_basis_title")}
            >
              {materialBasisOptions.map((u) => <option key={u} value={u}>{t("estimates_form.line_basis_per_unit", u)}</option>)}
            </select>
          </div>
          <div className="mt-1.5 flex items-center gap-1.5">
            <input
              data-testid={`line-material-qty-${idx}`}
              type="number"
              step="any"
              value={matQty}
              onChange={(e) => updateLine(idx, "material_qty", e.target.value)}
              className="w-20 text-right px-2 py-1 rounded border border-[var(--jc-border)] font-mono-jc"
              title={t("estimates_form.line_material_qty_title", matBasis)}
            />
            <span className="text-[var(--jc-text-soft)]">×</span>
            <span className="text-[var(--jc-text-soft)]">$</span>
            <input
              data-testid={`line-material-cost-${idx}`}
              type="number"
              step="any"
              value={line.material_cost}
              onChange={(e) => updateLine(idx, "material_cost", e.target.value)}
              className="w-24 text-right px-2 py-1 rounded border border-[var(--jc-border)] font-mono-jc"
            />
          </div>
          <div
            data-testid={`line-material-math-${idx}`}
            className="text-[11px] text-[var(--jc-text-soft)] mt-1.5"
          >
            {matMathTxt}
          </div>
        </div>
        )}

        {/* Labor */}
        <div className="rounded-md border border-[var(--jc-border)] bg-white p-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="overline">{t("estimates_form.line_labor")}</div>
            {isLaborOnlyRow && (
              <span
                data-testid={`line-labor-only-caption-${idx}`}
                className="text-[10px] font-semibold uppercase tracking-wide text-[var(--jc-text-soft)] px-1.5 py-0.5 rounded bg-[var(--jc-surface)]/60 border border-[var(--jc-border)]"
                title={t("estimates_form.line_labor_only_title")}
              >
                {t("estimates_form.line_labor_only_row")}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <select
              data-testid={`line-labor-basis-${idx}`}
              value={laborBasis}
              onChange={(e) => updateLine(idx, "labor_basis", e.target.value)}
              className="px-2 py-1 rounded border border-[var(--jc-border)] bg-white text-xs"
            >
              {laborBasisOptions.map((b) => (
                <option key={b} value={b}>
                  {b === "hour" ? t("estimates_form.line_per_hour") : b === "job" ? t("estimates_form.line_per_job") : t("estimates_form.line_per_basis", b)}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-1.5 flex items-center gap-1.5">
            <input
              data-testid={`line-labor-qty-${idx}`}
              type="number"
              step="any"
              value={line.labor_qty ?? 0}
              onChange={(e) => updateLine(idx, "labor_qty", e.target.value)}
              disabled={laborBasis === "job"}
              className="w-16 text-right px-2 py-1 rounded border border-[var(--jc-border)] font-mono-jc disabled:opacity-50"
              placeholder={t("estimates_form.line_qty_placeholder")}
            />
            <span className="text-[var(--jc-text-soft)]">×</span>
            <span className="text-[var(--jc-text-soft)]">$</span>
            <input
              data-testid={`line-labor-rate-${idx}`}
              type="number"
              step="any"
              value={line.labor_unit_cost ?? 0}
              onChange={(e) => updateLine(idx, "labor_unit_cost", e.target.value)}
              className="w-20 text-right px-2 py-1 rounded border border-[var(--jc-border)] font-mono-jc"
              placeholder={t("estimates_form.line_rate_placeholder")}
            />
          </div>
          <div
            data-testid={`line-labor-math-${idx}`}
            className="text-[11px] text-[var(--jc-text-soft)] mt-1.5"
          >
            {labMathTxt}
          </div>
        </div>
      </div>

      {/* Footer: markup + line total */}
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 text-sm">
          <span className="overline">{t("estimates_form.line_markup")}</span>
          <input
            data-testid={`line-markup-${idx}`}
            type="number"
            step="any"
            value={line.markup_pct}
            onChange={(e) => updateLine(idx, "markup_pct", e.target.value)}
            className="w-16 text-right px-2 py-1 rounded border border-[var(--jc-border)] font-mono-jc"
          />
          <span className="text-[var(--jc-text-soft)]">%</span>
        </div>
        <div className="text-right">
          <div className="overline">{t("estimates_form.line_total")}</div>
          <div
            data-testid={`line-total-${idx}`}
            className="font-mono-jc font-semibold text-lg"
          >
            {formatMoney(lineTotal)}
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

const PRICING_METHODS_KEYS = [
  { value: "", label_key: "pm_standard" },
  { value: "unit_price", label_key: "pm_unit_price" },
  { value: "flat", label_key: "pm_flat" },
];

const UNIT_TYPE_OPTIONS = [
  { value: "roofing_square", label: "Roofing square (100 sq ft)" },
  { value: "sq_ft", label: "Sq ft" },
  { value: "linear_ft", label: "Linear ft" },
  { value: "each", label: "Each" },
  { value: "room", label: "Room" },
  { value: "day", label: "Day" },
  { value: "hour", label: "Hour" },
  { value: "cubic_yd", label: "Cubic yd" },
  { value: "sheet", label: "Sheet" },
  { value: "roll", label: "Roll" },
  { value: "bundle", label: "Bundle" },
  { value: "flat", label: "Flat" },
];

const PricingMethodSelector = ({ idx, line, updateLine, isLaborOnlyRow = false, t }) => {
  const method = (line.pricing_method || "").trim();
  const isUnit = method === "unit_price";
  const isFlat = method === "flat";
  const q = Number(line.unit_quantity) || 0;
  const p = Number(line.unit_price) || 0;
  const fp = Number(line.flat_price) || 0;
  const preview = isUnit ? q * p : isFlat ? fp : null;
  // Roofing Simple Builder labor-package lines carry "labor package" in the
  // name. When we detect one, use roofer-friendly labels ("Roof area / labor
  // units", "Labor package rate") instead of generic ("Qty", "Unit price").
  // This is a display affordance only — the underlying fields, testids, and
  // math are unchanged.
  const isLaborPackage = isUnit && typeof line.name === "string" && line.name.toLowerCase().includes("labor package");
  const qtyLabel = isLaborPackage ? t("estimates_form.pm_qty_labor_pkg") : t("estimates_form.pm_qty");
  const priceLabel = isLaborPackage ? t("estimates_form.pm_labor_pkg_rate") : t("estimates_form.pm_unit_price_label");
  // iter31 A+ cosmetic — when the row is labor-only (material_required===false),
  // relabel the Standard option to "Standard (labor only)" so the dropdown
  // matches what the contractor sees below. Underlying value stays `""`, no
  // schema change, no new pricing_method.
  const pricingMethodOptions = PRICING_METHODS_KEYS.map((o) => ({
    value: o.value,
    label: o.value === "" && isLaborOnlyRow
      ? t("estimates_form.pm_standard_labor_only")
      : t(`estimates_form.${o.label_key}`),
  }));
  return (
    <div className="mt-3 rounded-md border border-[var(--jc-border)] bg-[var(--jc-surface)]/40 p-3" data-testid={`line-pricing-method-${idx}`}>
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--jc-text-soft)] mb-1.5">
        {t("estimates_form.pm_label")}
      </label>
      <select
        data-testid={`line-pricing-method-select-${idx}`}
        value={method}
        onChange={(e) => updateLine(idx, "pricing_method", e.target.value)}
        className="w-full text-sm px-2 py-1.5 rounded border border-[var(--jc-border)]"
      >
        {pricingMethodOptions.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {isUnit && (
        <div className="mt-3 grid grid-cols-4 gap-2" data-testid={`line-unit-price-block-${idx}`}>
          <div className="col-span-1">
            <label className="block text-[10px] font-semibold text-[var(--jc-text-soft)] mb-1">{qtyLabel}</label>
            <input
              type="number" step="0.01"
              data-testid={`line-unit-quantity-${idx}`}
              value={line.unit_quantity ?? ""}
              onChange={(e) => updateLine(idx, "unit_quantity", e.target.value)}
              className="w-full text-sm px-2 py-1.5 rounded border border-[var(--jc-border)]"
              placeholder="28"
            />
          </div>
          <div className="col-span-1">
            <label className="block text-[10px] font-semibold text-[var(--jc-text-soft)] mb-1">{t("estimates_form.pm_unit")}</label>
            <select
              data-testid={`line-unit-type-${idx}`}
              value={line.unit_type || ""}
              onChange={(e) => updateLine(idx, "unit_type", e.target.value)}
              className="w-full text-sm px-2 py-1.5 rounded border border-[var(--jc-border)]"
            >
              <option value="">{t("estimates_form.pm_unit_placeholder")}</option>
              {UNIT_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="col-span-1">
            <label className="block text-[10px] font-semibold text-[var(--jc-text-soft)] mb-1">{priceLabel}</label>
            <input
              type="number" step="0.01"
              data-testid={`line-unit-price-${idx}`}
              value={line.unit_price ?? ""}
              onChange={(e) => updateLine(idx, "unit_price", e.target.value)}
              className="w-full text-sm px-2 py-1.5 rounded border border-[var(--jc-border)]"
              placeholder="425.00"
            />
          </div>
          <div className="col-span-1">
            <label className="block text-[10px] font-semibold text-[var(--jc-text-soft)] mb-1">{t("estimates_form.pm_line_total")}</label>
            <div
              data-testid={`line-unit-total-${idx}`}
              className="w-full text-sm px-2 py-1.5 rounded border border-[var(--jc-border)] bg-white font-semibold font-mono"
            >
              {formatMoney(preview || 0)}
            </div>
          </div>
          <p className="col-span-4 text-[11px] text-[var(--jc-text-soft)] mt-1">
            {isLaborPackage
              ? t("estimates_form.pm_no_markup_labor_pkg")
              : t("estimates_form.pm_no_markup_default")}
          </p>
          {isLaborPackage && line._prefill_source && (
            <p
              className="col-span-4 text-[11px] text-[var(--jc-blue)] mt-1"
              data-testid={`line-prefill-hint-${idx}`}
            >
              {t("estimates_form.pm_prefill", line._prefill_source, line.unit_quantity, line.unit, line._prefill_converted)}
            </p>
          )}
        </div>
      )}
      {isFlat && (
        <div className="mt-3 grid grid-cols-2 gap-2" data-testid={`line-flat-block-${idx}`}>
          <div>
            <label className="block text-[10px] font-semibold text-[var(--jc-text-soft)] mb-1">{t("estimates_form.pm_flat_price")}</label>
            <input
              type="number" step="0.01"
              data-testid={`line-flat-price-${idx}`}
              value={line.flat_price ?? ""}
              onChange={(e) => updateLine(idx, "flat_price", e.target.value)}
              className="w-full text-sm px-2 py-1.5 rounded border border-[var(--jc-border)]"
              placeholder="750.00"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-[var(--jc-text-soft)] mb-1">{t("estimates_form.pm_line_total")}</label>
            <div
              data-testid={`line-flat-total-${idx}`}
              className="w-full text-sm px-2 py-1.5 rounded border border-[var(--jc-border)] bg-white font-semibold font-mono"
            >
              {formatMoney(preview || 0)}
            </div>
          </div>
          <p className="col-span-2 text-[11px] text-[var(--jc-text-soft)] mt-1">
            {t("estimates_form.pm_no_markup_flat")}
          </p>
        </div>
      )}
    </div>
  );
};

const MaterialIncompleteBanner = ({ counts, t }) => {
  if (!counts) return null;
  const { unpriced, needMaterial, total } = counts;
  if (total === 0 || (unpriced === 0 && needMaterial === 0)) return null;
  const parts = [];
  if (unpriced > 0) parts.push(t("estimates_form.mib_unpriced", unpriced));
  if (needMaterial > 0) parts.push(t("estimates_form.mib_need_material", needMaterial));
  return (
    <div
      data-testid="material-incomplete-banner"
      className="px-5 py-3 border-b border-yellow-300 bg-yellow-50 flex items-start gap-3"
    >
      <AlertTriangle size={16} className="text-yellow-700 mt-0.5 shrink-0" />
      <div className="flex-1 text-sm">
        <div className="font-semibold text-yellow-900">{t("estimates_form.mib_headline")}</div>
        <div className="text-yellow-800 mt-0.5">
          {parts.join(" · ")}{t("estimates_form.mib_body_suffix")}
        </div>
      </div>
    </div>
  );
};

const ScopeResultBanner = ({ banner, onDismiss, t }) => {
  const added = banner?.added ?? 0;
  if (added > 0) {
    return (
      <div
        data-testid="scope-banner-added"
        className="rounded-md border border-[var(--jc-blue)]/30 bg-[var(--jc-blue)]/5 p-4 flex items-start gap-3"
      >
        <Sparkles size={18} className="text-[var(--jc-blue)] mt-0.5" />
        <div className="flex-1 text-sm">
          <div className="font-semibold text-[var(--jc-text)]">
            {t("estimates_form.scope_banner_added_headline", added)}
          </div>
          <div className="text-[var(--jc-text-soft)] mt-0.5">
            {t("estimates_form.scope_banner_added_body_prefix")}<b>{t("estimates_form.scope_banner_added_body_drafts")}</b>{t("estimates_form.scope_banner_added_body_suffix")}<span className="font-semibold text-yellow-800">{t("estimates_form.scope_banner_needs_pricing")}</span>{t("estimates_form.scope_banner_added_body_dot")}
          </div>
        </div>
        <button
          onClick={onDismiss}
          data-testid="scope-banner-dismiss"
          className="text-xs font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"
        >
          {t("estimates_form.scope_banner_dismiss")}
        </button>
      </div>
    );
  }
  return (
    <div
      data-testid="scope-banner-empty"
      className="rounded-md border border-[var(--jc-border-strong)] bg-[var(--jc-bg)] p-4 flex items-start gap-3"
    >
      <Info size={18} className="text-[var(--jc-text-soft)] mt-0.5" />
      <div className="flex-1 text-sm">
        <div className="font-semibold text-[var(--jc-text)]">{t("estimates_form.scope_banner_empty_headline")}</div>
        <div className="text-[var(--jc-text-soft)] mt-0.5">
          {t("estimates_form.scope_banner_empty_body_prefix")}<b>{t("estimates_form.scope_banner_empty_body_add")}</b>{t("estimates_form.scope_banner_empty_body_mid")}<b>{t("estimates_form.scope_banner_empty_body_run")}</b>{t("estimates_form.scope_banner_empty_body_suffix")}
        </div>
      </div>
      <button
        onClick={onDismiss}
        data-testid="scope-banner-dismiss"
        className="text-xs font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"
      >
        {t("estimates_form.scope_banner_dismiss")}
      </button>
    </div>
  );
};

const Row = ({ label, value, children }) => (
  <div className="flex items-center justify-between">
    <dt className="text-[var(--jc-text-soft)]">{label}</dt>
    <dd className="font-mono-jc">{children || value}</dd>
  </div>
);

const AIScopePanel = ({ scope, t }) => (
  <section className="jc-card p-6 border-l-4 border-l-[var(--jc-blue)]">
    <div className="flex items-center gap-2">
      <Sparkles size={16} className="text-[var(--jc-blue)]" />
      <div className="overline">{t("estimates_form.aiscope_overline")}</div>
    </div>
    <h3 className="font-display text-lg mt-2">{t("estimates_form.aiscope_heading")}</h3>
    {scope.summary && <p className="text-sm mt-3 leading-relaxed">{scope.summary}</p>}
    <div className="grid md:grid-cols-2 gap-4 mt-4 text-sm">
      <Block title={t("estimates_form.aiscope_block_scope")} items={scope.organized_scope} />
      <Block title={t("estimates_form.aiscope_block_labor")} items={scope.labor_assumptions} />
      <Block title={t("estimates_form.aiscope_block_material")} items={scope.material_assumptions} />
      <Block title={t("estimates_form.aiscope_block_missing")} items={scope.missing_questions} />
      <Block title={t("estimates_form.aiscope_block_risk")} items={scope.risk_areas} />
    </div>
    {scope.customer_friendly_description && (
      <div className="mt-4 p-4 rounded-md bg-[var(--jc-bg)] border border-[var(--jc-border)] text-sm">
        <div className="overline mb-1">{t("estimates_form.aiscope_customer_desc")}</div>
        {scope.customer_friendly_description}
      </div>
    )}
  </section>
);

const Block = ({ title, items }) => {
  if (!items || !items.length) return null;
  return (
    <div>
      <div className="overline mb-1.5">{title}</div>
      <ul className="space-y-1 list-disc pl-5 text-[var(--jc-text)]">
        {items.map((x, i) => <li key={i}>{x}</li>)}
      </ul>
    </div>
  );
};

// ---------- Slice A3.1: Deterministic Double Check panel ----------
// Renders the persisted ``estimate.double_check`` payload as a Green /
// Yellow / Red review. Advisory only — this panel never affects the
// Generate Proposal button. Displays a "stale" indicator when the
// contractor has edited the estimate since the last Double Check run.
const DoubleCheckSeverityBadge = ({ severity, stale }) => {
  const { t } = useLang();
  const map = {
    green: { bg: "bg-green-100", tx: "text-green-800", label: t("estimates_form.dc_sev_green") },
    yellow: { bg: "bg-yellow-100", tx: "text-yellow-800", label: t("estimates_form.dc_sev_yellow") },
    red: { bg: "bg-red-100", tx: "text-red-800", label: t("estimates_form.dc_sev_red") },
  };
  const c = map[severity] || map.yellow;
  return (
    <span
      data-testid={`double-check-severity-${severity}`}
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${c.bg} ${c.tx}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${severity === "green" ? "bg-[var(--jc-green)]" : severity === "yellow" ? "bg-[var(--jc-yellow)]" : "bg-[var(--jc-red)]"}`} />
      {c.label}
      {stale && (
        <span
          data-testid="double-check-stale-indicator"
          className="ml-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--jc-text-soft)]"
        >
          {t("estimates_form.dc_stale_suffix")}
        </span>
      )}
    </span>
  );
};

const DoubleCheckPanel = ({ check, stale, onRerun, running, t: tProp }) => {
  const { t: tHook } = useLang();
  const t = tProp || tHook;
  const findings = check?.findings || [];
  const colorMap = {
    green: { bg: "bg-green-50", bd: "border-green-200", tx: "text-green-900", dot: "bg-[var(--jc-green)]" },
    yellow: { bg: "bg-yellow-50", bd: "border-yellow-200", tx: "text-yellow-900", dot: "bg-[var(--jc-yellow)]" },
    red: { bg: "bg-red-50", bd: "border-red-200", tx: "text-red-900", dot: "bg-[var(--jc-red)]" },
  };
  const ranAt = check?.ran_at ? new Date(check.ran_at).toLocaleString() : "";
  return (
    <section className="jc-card p-6" data-testid="double-check-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-[var(--jc-teal)]" />
            <div className="overline">{t("estimates_form.dc_overline")}</div>
            <DoubleCheckSeverityBadge severity={check?.overall_severity} stale={stale} />
          </div>
          <h3 className="font-display text-lg mt-2">{t("estimates_form.dc_heading")}</h3>
          <p className="text-xs text-[var(--jc-text-soft)] mt-1">
            {t("estimates_form.dc_body")}
            {ranAt && <>{t("estimates_form.dc_last_run", ranAt)}</>}
          </p>
        </div>
        <button
          onClick={onRerun}
          disabled={running}
          data-testid="double-check-rerun-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] disabled:opacity-60"
        >
          <ShieldCheck size={12} /> {running ? t("estimates_form.dc_rechecking") : stale ? t("estimates_form.dc_rerun_stale") : t("estimates_form.dc_rerun_run")}
        </button>
      </div>

      {stale && (
        <div
          data-testid="double-check-stale-banner"
          className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 text-yellow-900 px-4 py-3 text-sm"
        >
          {t("estimates_form.dc_stale_banner")}
        </div>
      )}

      {check?.ai_error && (
        <div
          data-testid="double-check-ai-error-note"
          className="mt-4 rounded-md border border-[var(--jc-border-strong)] bg-[var(--jc-bg)] text-[var(--jc-text)] px-4 py-3 text-sm"
        >
          <div className="flex items-start gap-2">
            <Sparkles size={14} className="text-[var(--jc-text-soft)] mt-0.5" />
            <div>
              <div className="font-semibold">{t("estimates_form.dc_ai_error_title")}</div>
              <div className="text-xs text-[var(--jc-text-soft)] mt-0.5">
                {t("estimates_form.dc_ai_error_body")}
              </div>
            </div>
          </div>
        </div>
      )}

      {findings.length === 0 ? (
        <div
          data-testid="double-check-empty-green"
          className="mt-4 rounded-md border border-green-200 bg-green-50 text-green-900 px-4 py-4 text-sm"
        >
          {t("estimates_form.dc_empty_green")}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          {findings.map((f, i) => {
            const c = colorMap[f.severity] || colorMap.yellow;
            const tr = renderFindingText(f, t);
            return (
              <div
                key={i}
                className={`rounded-md border ${c.bd} ${c.bg} ${c.tx} p-4`}
                data-testid={`double-check-finding-${f.severity}-${f.category}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold">{tr.categoryLabel}</div>
                  {f.source === "ai" && (
                    <span
                      data-testid="double-check-ai-chip"
                      className="ml-auto inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[var(--jc-blue)]/10 text-[var(--jc-blue)]"
                      title={t("estimates_form.dc_ai_chip_title")}
                    >
                      <Sparkles size={8} /> {t("estimates_form.dc_ai_chip")}
                    </span>
                  )}
                </div>
                <div className="font-semibold">{tr.title}</div>
                <div className="text-sm mt-1 opacity-90">{tr.detail}</div>
                {tr.suggestion && (
                  <div className="text-xs mt-2 italic opacity-80">{t("estimates_form.dc_suggestion_prefix")} {tr.suggestion}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};


// Slice 2D4 · Batch 1 Round 3 — Deterministic Double-Check findings carry a
// stable `template_key` + `context` payload from the backend so the
// contractor UI can render the same finding in the active UI language. Falls
// back to the backend-provided English (`f.title` / `f.detail` /
// `f.suggestion`) when there is no matching template — e.g. AI advisories
// (`f.source === "ai"`), prependFindings, or older estimates that predate
// this slice.
function renderFindingText(f, t) {
  const tpl = f?.template_key;
  const ctx = f?.context || {};
  let title = f?.title || "";
  let detail = f?.detail || "";
  let suggestion = f?.suggestion || "";

  if (tpl) {
    const tt = t(`estimates_form.dc_finding.${tpl}.title`);
    const td = t(`estimates_form.dc_finding.${tpl}.detail`);
    const ts = t(`estimates_form.dc_finding.${tpl}.suggestion`);
    const has = (v, key) => typeof v !== "undefined" && v !== `estimates_form.dc_finding.${tpl}.${key}`;
    if (has(tt, "title")) {
      title = typeof tt === "function"
        ? tpl === "incomplete_scope"
          ? tt(ctx.job_type, ctx.min_lines_minus_one, ctx.line_count)
          : tpl === "price_book_data_incomplete"
            ? tt(ctx.line_name)
            : tt(ctx.line_name)
        : tt;
    }
    if (has(td, "detail")) {
      detail = typeof td === "function"
        ? tpl === "incomplete_scope"
          ? td(ctx.job_type, ctx.min_lines_minus_one, ctx.line_count)
          : tpl === "price_book_data_incomplete"
            ? td(ctx.missing_fields)
            : td(ctx.line_name)
        : td;
    }
    if (has(ts, "suggestion")) {
      suggestion = typeof ts === "function" ? ts(ctx) : ts;
    }
  }

  // Category overline label (translated when we have a mapping).
  const catKey = `estimates_form.dc_finding.category.${f?.category}`;
  const catTr = t(catKey);
  const categoryLabel = catTr && catTr !== catKey ? catTr : (f?.category || f?.severity);

  return { title, detail, suggestion, categoryLabel };
}


const AICheckPanel = ({ check, prependFindings = [], t: tProp }) => {
  const { t: tHook } = useLang();
  const t = tProp || tHook;
  const findings = [...prependFindings, ...(check.findings || [])];
  const colorMap = {
    green: { bg: "bg-green-50", bd: "border-green-200", tx: "text-green-900", dot: "bg-[var(--jc-green)]" },
    yellow: { bg: "bg-yellow-50", bd: "border-yellow-200", tx: "text-yellow-900", dot: "bg-[var(--jc-yellow)]" },
    red: { bg: "bg-red-50", bd: "border-red-200", tx: "text-red-900", dot: "bg-[var(--jc-red)]" },
  };
  return (
    <section className="jc-card p-6">
      <div className="flex items-center gap-2">
        <ShieldCheck size={16} className="text-[var(--jc-teal)]" />
        <div className="overline">{t("estimates_form.aicheck_overline")}</div>
      </div>
      <h3 className="font-display text-lg mt-2">{t("estimates_form.aicheck_heading")}</h3>
      <div className="grid md:grid-cols-2 gap-3 mt-4">
        {findings.map((f, i) => {
          const c = colorMap[f.severity] || colorMap.yellow;
          const tr = renderFindingText(f, t);
          return (
            <div key={i} className={`rounded-md border ${c.bd} ${c.bg} ${c.tx} p-4`} data-testid={`finding-${f.severity}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold">{tr.categoryLabel}</div>
              </div>
              <div className="font-semibold">{tr.title}</div>
              <div className="text-sm mt-1 opacity-90">{tr.detail}</div>
              {tr.suggestion && <div className="text-xs mt-2 italic opacity-80">{t("estimates_form.aicheck_suggestion_prefix")} {tr.suggestion}</div>}
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ---------- Slice A2a: Price Book picker modal ----------
// Reuses the existing ``GET /api/material-price-book`` endpoint that Slice A1
// shipped. Purely a picker — never mutates the Price Book itself.
const PriceBookPickerModal = ({ onSelect, onClose, t: tProp }) => {
  const { t: tHook } = useLang();
  const t = tProp || tHook;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [trade, setTrade] = useState("");
  const [supplier, setSupplier] = useState("");
  const [brand, setBrand] = useState("");

  const fetchNow = async (overrides = {}) => {
    setLoading(true);
    try {
      const params = { active: "true" };
      const cur = { q, trade, supplier, brand, ...overrides };
      for (const k of ["trade", "supplier", "brand", "q"]) {
        if (cur[k]) params[k] = cur[k];
      }
      const { data } = await api.get("/material-price-book", { params });
      setItems(data || []);
    } catch {
      toast.error(t("estimates_form.toast_pb_load_failed"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchNow();
  }, []);
  useEffect(() => {
    fetchNow();
  }, [trade, supplier, brand]);

  const suppliers = Array.from(new Set(items.map((i) => i.supplier).filter(Boolean))).sort();
  const brands = Array.from(new Set(items.map((i) => i.brand).filter(Boolean))).sort();

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="pricebook-picker-modal"
    >
      <div className="jc-card w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--jc-border)]">
          <div>
            <h2 className="font-display text-xl tracking-tight">{t("estimates_form.pb_modal_title")}</h2>
            <div className="text-xs text-[var(--jc-text-soft)] mt-0.5">{t("estimates_form.pb_modal_body")}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="pricebook-picker-close-btn"
            className="p-1 hover:bg-[var(--jc-bg)] rounded"
            aria-label={t("estimates_form.pb_close")}
          >
            <X size={16} />
          </button>
        </div>

        {/* Filters */}
        <form
          onSubmit={(e) => { e.preventDefault(); fetchNow(); }}
          className="px-5 py-3 grid grid-cols-2 md:grid-cols-4 gap-2 border-b border-[var(--jc-border)] bg-[var(--jc-bg)]/40"
        >
          <select
            value={trade}
            onChange={(e) => setTrade(e.target.value)}
            data-testid="pricebook-picker-trade"
            className="px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
          >
            <option value="">{t("estimates_form.pb_all_trades")}</option>
            {["Roofing","Flooring","Painting","Drywall","Concrete","Restoration","Plumbing","HVAC"].map((tr) => (
              <option key={tr} value={tr}>{tr}</option>
            ))}
          </select>
          <select
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            data-testid="pricebook-picker-supplier"
            className="px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
          >
            <option value="">{t("estimates_form.pb_all_suppliers")}</option>
            {suppliers.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
          <select
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            data-testid="pricebook-picker-brand"
            className="px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
          >
            <option value="">{t("estimates_form.pb_all_brands")}</option>
            {brands.map((b) => (<option key={b} value={b}>{b}</option>))}
          </select>
          <div className="flex gap-1">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("estimates_form.pb_search_placeholder")}
              data-testid="pricebook-picker-q"
              className="flex-1 px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
            />
            <button
              type="submit"
              data-testid="pricebook-picker-search-btn"
              className="px-2 py-1.5 rounded border border-[var(--jc-border-strong)] hover:bg-white"
            >
              <Search size={14} />
            </button>
          </div>
        </form>

        {/* Results */}
        <div className="flex-1 overflow-y-auto" data-testid="pricebook-picker-results">
          {loading ? (
            <div className="p-6 text-sm text-[var(--jc-text-soft)]">{t("estimates_form.pb_loading")}</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-[var(--jc-text-soft)]" data-testid="pricebook-picker-empty">
              {t("estimates_form.pb_empty_prefix")}
              <a href="/app/pricing-profile/materials" target="_blank" rel="noreferrer" className="text-[var(--jc-blue)] hover:underline">
                {t("estimates_form.pb_empty_link")}
              </a>.
            </div>
          ) : (
            <ul className="divide-y divide-[var(--jc-border)]">
              {items.map((item) => (
                <li
                  key={item.id}
                  data-testid={`pricebook-picker-row-${item.id}`}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-[var(--jc-bg)]/60"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{item.product}</div>
                    <div className="text-[11px] text-[var(--jc-text-soft)]">
                      {item.trade}{item.system_type ? ` · ${item.system_type}` : ""}
                      {item.supplier ? ` · ${item.supplier}` : ""}
                      {item.brand ? ` · ${item.brand}` : ""}
                      {item.sku ? ` · ${item.sku}` : ""}
                    </div>
                  </div>
                  <div className="text-right text-xs whitespace-nowrap">
                    <div className="font-mono-jc font-semibold">${Number(item.contractor_cost || 0).toFixed(2)} / {item.unit || "each"}</div>
                    <div className="text-[10px] text-[var(--jc-text-soft)]">
                      {t("estimates_form.pb_waste", Number(item.waste_pct || 0).toFixed(1))}
                    </div>
                  </div>
                  <button
                    onClick={() => onSelect(item)}
                    data-testid={`pricebook-picker-select-${item.id}`}
                    className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded jc-btn-primary"
                  >
                    {t("estimates_form.pb_use")}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default EstimateDetail;
